import React, { useState, useEffect } from 'react';
import { peek as peekAuditImage, getOrFetch as getOrFetchAuditImage } from '../services/auditImageCache';
import useEagerAuditImage from '../hooks/useEagerAuditImage';
import AuditImg from './AuditImg';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Stack,
  Popover,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Card,
  CardContent,
  RadioGroup,
  Radio,
  Menu,
  InputAdornment,
  Snackbar,
  Alert,
  Collapse,
  Tooltip,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  CalendarMonth,
  Refresh as RefreshIcon,
  Close,
  Block as BlockIcon,
  RestartAlt as RestartAltIcon,
  Psychology,
  Login,
  Logout,
  AccessTime,
  ArrowUpward,
  ArrowDownward,
  ExpandLess,
  ExpandMore,
  PersonAddAlt1Outlined,
  AssignmentInd as AssignmentIndIcon,
  People as PeopleIcon,
  Block as IntrusionAttendanceIcon,
  BadgeOutlined as VisitorIcon,
  ChevronRight,
  HowToReg,
  SmartToy,
  CropFree as CropFreeIcon,
  Face6 as Face6Icon,
} from '@mui/icons-material';
import { DateRangePicker, Calendar } from 'react-date-range';
import { format, addDays, isSameDay } from 'date-fns';
import UniversalModal from './UniversalModal';
import { FindPersonSearchFromTileModal } from './FindPersonSearchFromTileModal';
import { formatDate } from '../utils/dateUtils';
import { sanitizeNameSearchInput, filterPersonNameOptions } from '../utils/nameSearchInput';
import { useImagePreview } from './ImagePreviewDialog';
import { EmployeeNameHyperlink } from './EmployeeNameHyperlink';
import { buildPersonLinkMeta } from '../utils/personDetailsNavigation';
import { getAttendanceMarkedByLabel, getAttendanceRevokedReopenHint, isAttendanceRevokedOnLog } from '../utils/attendanceDisplay';
import { getFaceImage, getPersonFaces } from '../utils/api';

/** Match employee pickers by name, employee_code, or person_id. */
const filterEmployeePickerOptions = (options, state) =>
  filterPersonNameOptions(options, state, { includePersonId: true });

const VisionReviewRecommendation = ({ visionReview, topCandidates = [] }) => {
  const candidates = Array.isArray(topCandidates) ? topCandidates.slice(0, 3) : [];
  const formatMatcherScore = (score) => {
    const n = Number(score);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
  };

  const renderMatcherSuggestions = () => {
    if (candidates.length === 0) return null;
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Face matcher suggestions:
        </Typography>
        {candidates.map((candidate, idx) => (
          <Typography key={candidate.person_id || idx} variant="body2" sx={{ fontSize: '0.85rem' }}>
            {idx + 1}. {candidate.name || 'Unknown'}
            {candidate.score != null ? ` (score ${formatMatcherScore(candidate.score)})` : ''}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderRanking = (ranking) => {
    if (!ranking.length) return null;
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          AI ranking:
        </Typography>
        {ranking.map((item, idx) => (
          <Typography key={`${item.candidate_index}-${idx}`} variant="body2" sx={{ fontSize: '0.85rem' }}>
            {idx + 1}. {item.employee_name || 'Unknown'}
            {item.confidence != null ? ` (${item.confidence}%)` : ''}
            {item.reason ? ` — ${item.reason}` : ''}
          </Typography>
        ))}
      </Box>
    );
  };

  if (visionReview?.error) {
    return (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        AI Matching failed: {visionReview.error}
        {renderMatcherSuggestions()}
      </Alert>
    );
  }

  const ranking = Array.isArray(visionReview?.ranking) ? visionReview.ranking : [];
  const reviewed = Boolean(visionReview?.reviewed);
  const matchFound = visionReview?.match_found;
  const hasAiPick = Boolean(visionReview?.best_candidate_name) || ranking.length > 0;

  if (!reviewed) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            AI Recommendation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI Matching has not been run yet.
          </Typography>
          {renderMatcherSuggestions() || (
            <Typography variant="body2" color="text.secondary">
              Use &quot;AI Matching&quot; to analyze this capture against the top candidates.
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  }

  if (matchFound === false || !hasAiPick) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            AI Recommendation: No match
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI Matching did not confirm a visual match. Mark attendance manually only if you are confident.
          </Typography>
          {renderRanking(ranking)}
          {renderMatcherSuggestions()}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          AI Recommendation{visionReview.best_candidate_name ? `: ${visionReview.best_candidate_name}` : ''}
        </Typography>
        {visionReview.confidence != null && (
          <Typography variant="body2" color="text.secondary">
            Confidence: {visionReview.confidence}%
          </Typography>
        )}
        {renderRanking(ranking)}
      </Stack>
    </Paper>
  );
};

// Helper to determine if attendance flag is truthy across different representations
const isAttendanceSent = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

/** Eager-load bucket capture thumbs in Mark Attendance Manually (session cache + fetch). */
const BucketManualMarkFrameImage = ({ auditId, fallbackSrc = null, anonymousImage = '/anonymous.webp' }) => {
  const { url } = useEagerAuditImage(auditId, Boolean(auditId));
  const resolved =
    (fallbackSrc && fallbackSrc !== anonymousImage ? fallbackSrc : null) ||
    (url && url !== anonymousImage ? url : null);

  if (!resolved && url === undefined) {
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={resolved || anonymousImage}
      alt="Bucket capture"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
    />
  );
};

/**
 * Resolved image URL for Processing timeline frames. Prefer a non-placeholder direct `frame.image`,
 * then audit snapshot cache (`audit_id`), then attendance file cache (`attendance:${attendance_id}`).
 * Guards against treating ANONYMOUS_IMAGE as a real image so cached snapshots are not skipped.
 */
export const resolveProcessingTimelineFrameSrc = (frame, imageCache = {}, ANONYMOUS_IMAGE = '/anonymous.webp') => {
  const log = frame?.log;
  if (!log) return ANONYMOUS_IMAGE;
  const anon = ANONYMOUS_IMAGE;
  const direct = frame?.image;
  const usableDirect = direct && direct !== anon ? direct : null;

  const auditId = log.audit_id != null ? String(log.audit_id).trim() : '';
  const sessionAudit = auditId ? peekAuditImage(auditId) : undefined;
  const fromAuditCache = auditId && imageCache?.[auditId] && imageCache[auditId] !== anon
    ? imageCache[auditId]
    : null;
  const fromAuditSession =
    sessionAudit !== undefined && sessionAudit !== null && sessionAudit !== anon
      ? sessionAudit
      : null;
  const fromAudit = fromAuditCache || fromAuditSession;

  let meta = log.request_metadata;
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = null;
    }
  }
  const manualNested = meta && typeof meta === 'object' ? meta.manual_attendance : null;
  const attendanceIdRaw =
    (log.manual_attendance && log.manual_attendance.attendance_id) ??
    (manualNested && manualNested.attendance_id) ??
    log.attendance_id ??
    (meta && meta.attendance && meta.attendance.attendance_id);
  const attendanceId = attendanceIdRaw != null && String(attendanceIdRaw).trim() !== '' ? String(attendanceIdRaw).trim() : '';
  const attKey = attendanceId ? `attendance:${attendanceId}` : '';
  const fromAttendance =
    attKey && imageCache?.[attKey] && imageCache[attKey] !== anon ? imageCache[attKey] : null;

  return usableDirect || fromAudit || fromAttendance || anon;
};

const resolveCapturedFramePreviewSrc = async (frame, imageCache = {}, ANONYMOUS_IMAGE = '/anonymous.webp') => {
  const resolved = resolveProcessingTimelineFrameSrc(frame, imageCache, ANONYMOUS_IMAGE);
  if (resolved && resolved !== ANONYMOUS_IMAGE) return resolved;

  const auditId = frame?.log?.audit_id != null ? String(frame.log.audit_id).trim() : '';
  if (!auditId) return null;

  const cached = peekAuditImage(auditId);
  if (cached) return cached;

  try {
    const fetched = await getOrFetchAuditImage(auditId);
    return fetched || null;
  } catch {
    return null;
  }
};

const POSE_FIELD_DEFS = [
  { key: 'face_direction', label: 'Face Direction', aliases: ['pose_face_direction'] },
  { key: 'face_movement', label: 'Face Movement', aliases: ['pose_face_movement'] },
  { key: 'body_direction', label: 'Body Direction', aliases: ['pose_body_direction'] },
  { key: 'body_movement', label: 'Body Movement', aliases: ['pose_body_movement'] },
];

const getResultMeta = (log) => {
  if (!log) return { label: 'Unknown', color: 'default' };
  if (log.success) return { label: 'Matched', color: 'success' };
  if (log.confidence_level === 'possible_match') return { label: 'Possible Match', color: 'warning' };
  if (log.confidence_level === 'no_match') return { label: 'Unmatched', color: 'error' };
  return { label: 'Failed', color: 'error' };
};

const isMatchOrPossibleMatchLog = (log) => (
  Boolean(log?.success) || log?.confidence_level === 'possible_match'
);

const resolveEnrolledPersonId = (log) => {
  if (!log) return '';
  const direct = String(log.recognized_person_id || log.person_id || '').trim();
  if (direct) return direct;
  if (log.confidence_level !== 'possible_match') return '';
  const firstCandidate = Array.isArray(log.request_metadata?.top_candidates)
    ? log.request_metadata.top_candidates.find((candidate) => String(candidate?.person_id || '').trim())
    : null;
  return firstCandidate?.person_id ? String(firstCandidate.person_id).trim() : '';
};

const pickPreferredEnrolledFace = (faces) => {
  const faceList = Array.isArray(faces) ? faces : [];
  if (!faceList.length) return null;
  const sortByPoseDesc = (a, b) => Number(b?.pose_quality || 0) - Number(a?.pose_quality || 0);
  const frontFaces = faceList
    .filter((face) => String(face?.face_angle || '').toLowerCase() === 'front')
    .sort(sortByPoseDesc);
  return frontFaces[0] || [...faceList].sort(sortByPoseDesc)[0] || faceList[0];
};

const getMatchedProfileLabel = (log) => (
  log?.request_metadata?.matched_angle ||
  log?.matched_angle ||
  null
);

const formatAuditAngleLabel = (angle) => {
  if (!angle) return null;
  const value = String(angle).toLowerCase();
  if (value === 'left_profile') return 'left';
  if (value === 'right_profile') return 'right';
  if (value === 'custom' || value === 'custom_angle' || value.startsWith('custom_angle_')) return 'custom';
  return value;
};

const normaliseMs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Math.trunc(num);
};

const msToIsoString = (value) => {
  const num = normaliseMs(value);
  if (num === null) return '';
  const date = new Date(num);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const parseIsoToEpochMs = (value) => {
  if (!value && value !== 0) return Number.NaN;
  const text = String(value).trim();
  if (!text) return Number.NaN;
  const hasTimezone = /([+-]\d{2}:?\d{2}|Z)$/i.test(text);
  const normalizedText = hasTimezone ? text : `${text}Z`;
  const parsed = Date.parse(normalizedText);
  if (!Number.isNaN(parsed)) return parsed;
  return Number.NaN;
};

const resolveTimestamp = (isoValue, msValue, fallbackIso) => {
  let resolvedIso = isoValue || '';
  let resolvedMs = normaliseMs(msValue);
  if (!resolvedIso) {
    const fromMs = msToIsoString(resolvedMs);
    if (fromMs) resolvedIso = fromMs;
  }
  if (!resolvedIso && fallbackIso) {
    resolvedIso = fallbackIso;
  }
  if ((resolvedMs === null || resolvedMs === undefined) && resolvedIso) {
    const parsed = parseIsoToEpochMs(resolvedIso);
    if (!Number.isNaN(parsed)) resolvedMs = parsed;
  }
  if (resolvedMs !== null && resolvedMs !== undefined) {
    const canonicalIso = msToIsoString(resolvedMs);
    if (canonicalIso) resolvedIso = canonicalIso;
  } else if (resolvedIso) {
    const parsed = parseIsoToEpochMs(resolvedIso);
    if (!Number.isNaN(parsed)) {
      resolvedMs = parsed;
      const canonicalIso = msToIsoString(resolvedMs);
      if (canonicalIso) resolvedIso = canonicalIso;
    }
  }
  return { iso: resolvedIso, ms: resolvedMs };
};

const getFrameCapturedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.captured_at || meta.captured_at,
    log.captured_at_ms ?? meta.captured_at_ms,
    log.created_at
  );
};

const getFrameRequestSentTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log?.recognize_started_at ?? meta.recognize_started_at,
    log?.recognize_started_at_ms ?? meta.recognize_started_at_ms ?? null, null
  );
}

const getFrameApiSubmitTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.api_submit_at || meta.api_submit_at,
    log.api_submit_at_ms ?? meta.api_submit_at_ms,
    log.created_at
  );
};

const getFrameApiCompletedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.api_completed_at ?? meta.api_completed_at ?? null,
    log.api_completed_at_ms ?? meta.api_completed_at_ms ?? null,
    null
  );
};

const getFrameMatchingStartedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  const matchingStarted = resolveTimestamp(
    log.matching_started_at ?? meta.matching_started_at ?? null,
    log.matching_started_at_ms ?? meta.matching_started_at_ms ?? null,
    null
  );
  if (matchingStarted.iso || matchingStarted.ms != null) return matchingStarted;
  return getFrameRequestSentTimestamp(log);
};

const getMatchingStartToCompletedDeltaMs = (matchingStartedAtMs, matchingCompletedAtMs) => {
  if (matchingStartedAtMs == null || matchingCompletedAtMs == null) return null;
  const started = Number(matchingStartedAtMs);
  const completed = Number(matchingCompletedAtMs);
  if (Number.isNaN(started) || Number.isNaN(completed)) return null;
  if (completed < started) return null;
  return completed - started;
};

const formatTimingValue = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return `${num.toFixed(1)} ms`;
};

const getPoseMetrics = (log) => {
  const meta = log.request_metadata || {};
  const metrics = {};
  POSE_FIELD_DEFS.forEach(({ key, aliases }) => {
    const candidates = [key, ...(aliases || [])];
    for (const candidate of candidates) {
      const resolved = log[candidate] ?? meta[candidate];
      if (resolved === undefined || resolved === null || resolved === '') continue;
      metrics[key] = String(resolved);
      break;
    }
  });
  return metrics;
};

const resolveMsValue = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.ms !== undefined && timestamp.ms !== null && !Number.isNaN(Number(timestamp.ms))) {
    return Number(timestamp.ms);
  }
  if (timestamp.iso) {
    const parsed = Date.parse(timestamp.iso);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDurationMs = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '';
  return `${Math.abs(ms)}ms`;
};

const formatTimelineTimestamp = (timestamp) => {
  const value = resolveMsValue(timestamp) ?? timestamp?.iso;
  if (value === null || value === undefined || value === '') return '—';
  return formatDate(value, true, true, false, true);
};

const FlowTimeline = ({ steps }) => {
  const enriched = steps.map((step, idx) => {
    const prev = steps[idx - 1];
    const hasValue = step.timestamp && (step.timestamp.iso || step.timestamp.ms != null);
    const currentMs = resolveMsValue(step.timestamp);
    const prevMs = resolveMsValue(prev?.timestamp);
    const rawDuration = currentMs !== null && prevMs !== null ? currentMs - prevMs : null;
    const duration = step.durationOverrideMs != null
      ? step.durationOverrideMs
      : (rawDuration != null && rawDuration >= 0 ? rawDuration : null);
    return { ...step, hasValue, duration };
  });

  return (
    <Stack direction="row" spacing={1.5} alignItems="stretch" flexWrap="nowrap" sx={{ overflowX: 'auto', pb: 1 }}>
      {enriched.map((step, idx) => (
        <React.Fragment key={step.label}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              minWidth: 170,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  fontWeight: 700,
                }}
              >
                {step.label}
              </Typography>
              {step.showCropIcon ? (
                <CropFreeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              ) : null}
              {step.showFaceIcon ? (
                <Face6Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
              ) : null}
            </Stack>
            <Typography variant="body2">
              {step.hasValue ? formatTimelineTimestamp(step.timestamp) : '—'}
            </Typography>
          </Paper>
          {idx < enriched.length - 1 && (
            <Stack alignItems="center" justifyContent="center" sx={{ minWidth: 90, px: 1, flexShrink: 0 }}>
              {enriched[idx + 1].duration !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  Δ {formatDurationMs(enriched[idx + 1].duration)}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>→</Typography>
            </Stack>
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
};

const resolveAuditLogField = (log, key) => {
  const topLevel = log?.[key];
  if (topLevel !== undefined && topLevel !== null && topLevel !== '') return topLevel;
  const metaValue = log?.request_metadata?.[key];
  if (metaValue !== undefined && metaValue !== null && metaValue !== '') return metaValue;
  return log?.timing_metrics?.[key];
};

const resolveAuditLogCameraType = (log) => {
  const candidates = [
    log?.camera,
    log?.camera_type,
    log?.request_metadata?.camera_type,
    log?.request_metadata?.x_camera_type,
  ];
  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim().toUpperCase();
    if (normalized === 'IN' || normalized === 'OUT') return normalized;
    if (normalized.startsWith('IN')) return 'IN';
    if (normalized.startsWith('OUT')) return 'OUT';
  }
  return null;
};

const getMovementDirectionChipColor = (movementDirection, cameraType) => {
  const normalizedDirection = String(movementDirection || '').trim().toUpperCase();
  const normalizedCamera = String(cameraType || '').trim().toUpperCase();
  if (!normalizedDirection) return 'default';

  if (normalizedCamera === 'IN') {
    return normalizedDirection === 'MOVING_IN' || normalizedDirection === 'STATIC'
      ? 'success'
      : 'error';
  }

  if (normalizedCamera === 'OUT') {
    return normalizedDirection === 'MOVING_OUT' || normalizedDirection === 'STATIC'
      ? 'success'
      : 'error';
  }

  return 'default';
};

const parseTimingMs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const getCaptureToWorkerMs = (log) => {
  const capturedMs = resolveMsValue(getFrameCapturedTimestamp(log));
  const submitMs = resolveMsValue(getFrameApiSubmitTimestamp(log));
  if (capturedMs != null && submitMs != null && submitMs >= capturedMs) {
    return submitMs - capturedMs;
  }
  const fallback = parseTimingMs(resolveAuditLogField(log, 'timing_capture_to_worker_ms'));
  return fallback;
};

const getDetectionTimeTotalMs = (log, addendFields) => {
  const addends = addendFields.map(({ key }) => parseTimingMs(resolveAuditLogField(log, key)));
  if (addends.some((value) => value != null)) {
    return addends.reduce((sum, value) => sum + (value || 0), 0);
  }
  return getCaptureToWorkerMs(log);
};

const renderTimingOperator = (symbol) => (
  <Typography
    variant="h6"
    component="span"
    sx={{
      fontWeight: 700,
      color: 'text.secondary',
      alignSelf: 'flex-end',
      pb: 0.15,
      lineHeight: 1,
      userSelect: 'none',
    }}
  >
    {symbol}
  </Typography>
);

const renderTimingMetricBox = (label, value, emphasize = false) => (
  <Box sx={{ minWidth: emphasize ? 140 : 110 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: emphasize ? 700 : 600 }}>
      {formatTimingValue(value)}
    </Typography>
  </Box>
);

const renderTimingMetrics = (log) => {
  const addendFields = [
    { key: 'timing_frame_prep_ms', label: 'Frame Prep' },
    { key: 'timing_detection_ms', label: 'Detection' },
    { key: 'timing_queue_prep_ms', label: 'Queue Prep' },
    { key: 'timing_queue_wait_ms', label: 'Queue Wait' },
  ];

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1.25}
      alignItems="flex-end"
      sx={{ rowGap: 1 }}
    >
      {addendFields.map(({ key, label }, idx) => (
        <React.Fragment key={key}>
          {idx > 0 ? renderTimingOperator('+') : null}
          {renderTimingMetricBox(label, resolveAuditLogField(log, key))}
        </React.Fragment>
      ))}
      {renderTimingOperator('=')}
      {renderTimingMetricBox('Total', getDetectionTimeTotalMs(log, addendFields), true)}
    </Stack>
  );
};

const renderPoseMetrics = (metrics) => (
  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ rowGap: 1 }}>
    {POSE_FIELD_DEFS.map(({ key, label }) => {
      const value = metrics[key];
      if (value === undefined) return null;
      return (
        <Box key={key} sx={{ minWidth: 130 }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{value}</Typography>
        </Box>
      );
    })}
  </Stack>
);

export const ProcessingTimelineFrameDetailsCard = ({
  frame,
  imageCache,
  ANONYMOUS_IMAGE,
  showProcessingTimeline = true,
  showTimingHeading = true,
  showTopMatches = true,
  statusLabel = null,
  statusChipSx = null,
  onFrameImageClick = null,
}) => {
  const { log } = frame;
  const [isExpanded, setIsExpanded] = useState(false);
  const hasFrameAttendance = isAttendanceSent(log?.send_attendance);
  const capturedTs = getFrameCapturedTimestamp(log);
  const requestSentTs = getFrameRequestSentTimestamp(log);
  const apiSubmitTs = getFrameApiSubmitTimestamp(log);
  const matchingStartedTs = getFrameMatchingStartedTimestamp(log);
  const apiCompletedTs = getFrameApiCompletedTimestamp(log);
  const matchingStartToCompletedDeltaMs = getMatchingStartToCompletedDeltaMs(
    resolveMsValue(matchingStartedTs),
    resolveMsValue(apiCompletedTs)
  );
  const resultMeta = getResultMeta(log);
  const displayStatusLabel = statusLabel || resultMeta.label;
  const usesCustomStatusChip = Boolean(statusLabel || statusChipSx);
  const matchedProfileLabel = formatAuditAngleLabel(getMatchedProfileLabel(log));
  const frameAuditId = log?.audit_id != null ? String(log.audit_id).trim() : '';
  const frameImage = resolveProcessingTimelineFrameSrc(frame, imageCache, ANONYMOUS_IMAGE);
  const timelineSteps = [
    { label: 'Face Captured', timestamp: capturedTs, showCropIcon: true },
    { label: 'Recognition Submit', timestamp: apiSubmitTs, showCropIcon: true },
    { label: 'Matching Queued', timestamp: requestSentTs, showFaceIcon: true },
    { label: 'Matching Started', timestamp: matchingStartedTs, showFaceIcon: true },
    { label: 'Matching Completed', timestamp: apiCompletedTs, durationOverrideMs: matchingStartToCompletedDeltaMs, showFaceIcon: true },
  ];
  const hasTimelineData = timelineSteps.some((step) => (
    step?.timestamp && (step.timestamp.iso || step.timestamp.ms != null)
  ));
  const framePoseMetrics = getPoseMetrics(log);
  const hasFramePose = POSE_FIELD_DEFS.some(({ key }) => framePoseMetrics[key] !== undefined);
  const totalStart = resolveMsValue(capturedTs) || resolveMsValue(requestSentTs);
  const totalEnd = resolveMsValue(apiCompletedTs);
  const totalTimeMs = totalStart != null && totalEnd != null && totalEnd >= totalStart
    ? totalEnd - totalStart
    : null;
  const faceDetWidthHeight =
  (log.request_metadata?.face_detection_width != null && log.request_metadata?.face_detection_height != null
    ? `${log.request_metadata.face_detection_width}x${log.request_metadata.face_detection_height}`
    : null);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fff', boxShadow: '0 12px 30px rgba(15,23,42,0.08)' }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            data-no-image-preview="true"
            sx={{
              width: { xs: 72, sm: 96 },
              height: { xs: 72, sm: 96 },
              borderRadius: 1.5,
              overflow: 'hidden',
              background: '#0f172a',
              flexShrink: 0,
              cursor: typeof onFrameImageClick === 'function' ? 'pointer' : 'default',
            }}
            onClick={(event) => {
              if (typeof onFrameImageClick !== 'function') return;
              event.preventDefault();
              event.stopPropagation();
              onFrameImageClick(frame);
            }}
          >
            {frameAuditId ? (
              <AuditImg
                auditId={frameAuditId}
                alt={log.recognized_name || 'Frame'}
                anonymousImage={ANONYMOUS_IMAGE || '/anonymous.webp'}
                sx={{ width: '100%', height: '100%', pointerEvents: 'none' }}
                imgSx={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
              />
            ) : (
              <Box
                component="img"
                src={frameImage || ANONYMOUS_IMAGE || '/anonymous.webp'}
                alt={log.recognized_name || 'Frame'}
                data-no-image-preview="true"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>
          <Stack spacing={0.5} flex={1}>
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {log.recognized_name || 'Unknown person'}
              </Typography>
              <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0.5, '&:hover': { bgcolor: 'action.hover' } }}>
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
              <Chip
                size="small"
                label={displayStatusLabel}
                color={
                  usesCustomStatusChip
                    ? 'default'
                    : (resultMeta.color === 'default' ? 'default' : resultMeta.color)
                }
                variant={
                  usesCustomStatusChip
                    ? 'filled'
                    : (resultMeta.color === 'default' ? 'outlined' : 'filled')
                }
                sx={{ fontSize: '0.7rem', height: 22, ...(statusChipSx || {}) }}
              />
              {log.track_id != null && <Chip size="small" label={`Track ${log.track_id}`} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
              {log.camera && <Chip size="small" label={`Camera ${(log.camera || '').toUpperCase()}`} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
              {log.operation_type && <Chip size="small" label={log.operation_type} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
              {hasFrameAttendance && <Chip size="small" label="Attendance" color="success" variant="filled" sx={{ fontSize: '0.6rem', height: 20 }} />}
              {log.is_intrusion && <Chip size="small" label="Intrusion" color="warning" variant="filled" sx={{ fontSize: '0.6rem', height: 20, fontWeight: 600 }} />}
              {log.confidence_score != null && log.confidence_score !== '' && (
                <Chip
                  size="small"
                  label={`Match Score : ${(Math.floor(parseFloat(log.confidence_score) * 100) / 100).toFixed(2)}`}
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {matchedProfileLabel && (
                <Chip
                  size="small"
                  label={`Profile: ${matchedProfileLabel}`}
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {!!log.request_metadata?.detection_confidence && (
                <Chip
                  size="small"
                  label={`Face Conf: ${(Math.floor(parseFloat(log.request_metadata.detection_confidence) * 100) / 100).toFixed(2)}`}
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
              {faceDetWidthHeight && (
                  <Chip
                    size="small"
                    label={`Dimensions: ${faceDetWidthHeight}`}
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
              )}
              {(() => {
                const laneValue =
                  log?.lane_number ??
                  log?.request_metadata?.lane_number ??
                  log?.request_metadata?.laneNumber;
                if (laneValue === null || laneValue === undefined || laneValue === '') return null;
                return <Chip size="small" label={`Lane: ${laneValue}`} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />;
              })()}
              {(() => {
                const movementDirection = resolveAuditLogField(log, 'movement_direction');
                if (!movementDirection) return null;
                const movementChipColor = getMovementDirectionChipColor(
                  movementDirection,
                  resolveAuditLogCameraType(log),
                );
                return (
                  <Chip
                    size="small"
                    label={`Movement: ${movementDirection}`}
                    color={movementChipColor === 'default' ? undefined : movementChipColor}
                    variant={movementChipColor === 'default' ? 'outlined' : 'filled'}
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
                );
              })()}
              {(() => {
                const tagValue = resolveAuditLogField(log, 'tag');
                if (!tagValue) return null;
                return (
                  <Chip
                    size="small"
                    label={`Tag: ${tagValue}`}
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
                );
              })()}
              {(() => {
                const firstFrameInRoi = resolveAuditLogField(log, 'first_frame_in_roi');
                if (firstFrameInRoi === null || firstFrameInRoi === undefined || firstFrameInRoi === '') {
                  return null;
                }
                const isFirstFrame =
                  firstFrameInRoi === true ||
                  firstFrameInRoi === 1 ||
                  String(firstFrameInRoi).trim().toLowerCase() === 'true' ||
                  String(firstFrameInRoi).trim() === '1';
                return (
                  <Chip
                    size="small"
                    label={`First Frame ROI: ${isFirstFrame ? 'Yes' : 'No'}`}
                    color={isFirstFrame ? 'success' : 'error'}
                    variant="filled"
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
                );
              })()}
            </Stack>
            {(log.employee_code || log.recognized_person_id) && (
              <Typography variant="caption" color="text.secondary">
                ID: {log.employee_code || log.recognized_person_id}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Collapse in={isExpanded} timeout={300} easing="ease-in-out">
          <Divider />
          <Box sx={{ mt: 2 }}>
            {showProcessingTimeline && hasTimelineData && (
              <>
                <FlowTimeline steps={timelineSteps} />
                <Divider sx={{ my: 1.5 }} />
              </>
            )}
            {hasFramePose && (
              <>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                    Pose info
                  </Typography>
                  {renderPoseMetrics(framePoseMetrics)}
                </Stack>
                <Divider sx={{ my: 1.5 }} />
              </>
            )}
            <Stack spacing={0.5}>
              {showTimingHeading && (
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                  Detection Time (ms)
                </Typography>
              )}
              {renderTimingMetrics(log)}
            </Stack>
            {showTopMatches && (log.confidence_level === 'possible_match' || log.confidence_level === 'no_match') &&
              Array.isArray(log.request_metadata?.top_candidates) &&
              log.request_metadata.top_candidates.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                      Top matches
                    </Typography>
                    {log.request_metadata.top_candidates.slice(0, 3).map((candidate, idx) => (
                      <Typography key={candidate.person_id || idx} variant="body2" sx={{ color: 'text.secondary' }}>
                        {idx + 1}. {candidate.name || 'Unknown'} — score: {Number(candidate.score).toFixed(2)}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
          </Box>
        </Collapse>
        <Divider />
        {(capturedTs && totalStart != null && totalTimeMs != null) && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {capturedTs.iso && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {capturedTs.iso ? formatDate(capturedTs.iso,true,true,false,true) : '-'}
              </Typography>
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Total Time: {totalTimeMs.toFixed(0)} ms
            </Typography>
          </Stack>
        )}
        {getAttendanceMarkedByLabel(log) && (
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {getAttendanceMarkedByLabel(log)}
          </Typography>
        )}
        {log.manual_attendance?.remark && (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Remark: {log.manual_attendance.remark}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const MANUAL_PERSON_AUTOCOMPLETE_SLOT_PROPS = {
  popper: {
    placement: 'bottom-start',
    modifiers: [
      { name: 'flip', enabled: false },
      { name: 'preventOverflow', enabled: true, options: { altAxis: true, tether: true } },
    ],
    sx: { zIndex: 1500 },
  },
  listbox: {
    sx: { maxHeight: 240 },
  },
  paper: {
    sx: { mt: 0.5, boxShadow: 3 },
  },
};

const PossibleMatchManualPersonSelect = ({
  companyOptions = [],
  manualCompanyId = '',
  setManualCompanyId = () => {},
  selectedPersonId = null,
  setSelectedPersonId = () => {},
  sortedEmployees = [],
  employeesLoading = false,
  loadEmployees = () => {},
  topCandidateIds = new Set(),
  sessionKey = '',
}) => {
  const [manualMarkExpanded, setManualMarkExpanded] = useState(false);
  const effectiveCompanyId =
    manualCompanyId || (companyOptions.length === 1 ? companyOptions[0]?.company_id || '' : '');
  const effectiveCompanyName =
    companyOptions.find((cfg) => String(cfg.company_id || '') === String(effectiveCompanyId))?.company_name ||
    companyOptions[0]?.company_name ||
    'Default Company';
  const requiresCompany = companyOptions.length > 1;
  const isManualSelection =
    selectedPersonId && !topCandidateIds.has(String(selectedPersonId));

  useEffect(() => {
    setManualMarkExpanded(false);
  }, [sessionKey]);

  const handleOpenManualMark = () => {
    setManualMarkExpanded(true);
    if (effectiveCompanyId) {
      loadEmployees(effectiveCompanyId);
    }
  };

  const handleCloseManualMark = () => {
    setManualMarkExpanded(false);
    if (isManualSelection) {
      setSelectedPersonId(null);
    }
    setManualCompanyId('');
  };

  return (
    <Box sx={{ pt: 0.5 }}>
      {!manualMarkExpanded ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<HowToReg />}
          onClick={handleOpenManualMark}
          sx={{ textTransform: 'none' }}
        >
          Manually mark
        </Button>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Mark for another person
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Select company and person if they are not listed in the top matchers.
                </Typography>
              </Box>
              <Button
                size="small"
                color="inherit"
                onClick={handleCloseManualMark}
                sx={{ flexShrink: 0, minWidth: 'auto', color: 'text.secondary' }}
              >
                Cancel
              </Button>
            </Stack>

            {companyOptions.length > 0 && (
              requiresCompany ? (
                <FormControl fullWidth size="small">
                  <InputLabel id="possible-match-manual-company-label">Company</InputLabel>
                  <Select
                    labelId="possible-match-manual-company-label"
                    value={effectiveCompanyId}
                    label="Company"
                    MenuProps={{
                      PaperProps: { sx: { maxHeight: 280 } },
                      sx: { zIndex: 1500 },
                    }}
                    onChange={(e) => {
                      setManualCompanyId(e.target.value);
                      setSelectedPersonId(null);
                      loadEmployees(e.target.value);
                    }}
                  >
                    <MenuItem value="">
                      <em>Select company</em>
                    </MenuItem>
                    {companyOptions.map((cfg) => (
                      <MenuItem key={cfg.config_id || cfg.company_id || 'default-possible-match'} value={cfg.company_id || ''}>
                        {cfg.company_name || 'Default Company'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Company"
                  size="small"
                  fullWidth
                  value={effectiveCompanyName}
                  InputProps={{ readOnly: true }}
                />
              )
            )}

            <Autocomplete
              options={sortedEmployees}
              loading={employeesLoading}
              disabled={requiresCompany && !effectiveCompanyId}
              openOnFocus
              slotProps={MANUAL_PERSON_AUTOCOMPLETE_SLOT_PROPS}
              filterOptions={filterEmployeePickerOptions}
              getOptionLabel={(option) => {
                const code = option?.employee_code ? ` (${option.employee_code})` : '';
                return `${option?.name || 'Unknown'}${code}`;
              }}
              isOptionEqualToValue={(option, value) =>
                String(option?.person_id) === String(value?.person_id)
              }
              value={
                isManualSelection
                  ? sortedEmployees.find((e) => String(e.person_id) === String(selectedPersonId)) || null
                  : null
              }
              onChange={(e, newValue) => {
                setSelectedPersonId(newValue ? newValue.person_id : null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select person"
                  size="small"
                  placeholder={requiresCompany && !effectiveCompanyId ? 'Select company first' : 'Search by name, ID, or Employee Code'}
                  helperText={
                    requiresCompany && !effectiveCompanyId
                      ? 'Choose a company first to load employees.'
                      : 'Type to search employees for the selected company.'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {employeesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

const timingDialogShimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const timingDialogShimmerSx = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${timingDialogShimmer} 1.4s ease-in-out infinite`,
};

const TimingDialogChipShimmer = ({ width = 72 }) => (
  <Box
    sx={{
      width,
      height: { xs: 22, sm: 24 },
      borderRadius: 12,
      ...timingDialogShimmerSx,
    }}
  />
);

const TimingDialogFrameCardShimmer = () => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: '#fff',
      boxShadow: '0 12px 30px rgba(15,23,42,0.06)',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: { xs: 72, sm: 96 },
          height: { xs: 72, sm: 96 },
          borderRadius: 1.5,
          flexShrink: 0,
          ...timingDialogShimmerSx,
        }}
      />
      <Stack spacing={1} flex={1} sx={{ minWidth: 0, pt: 0.25 }}>
        <Box sx={{ width: '48%', height: 20, borderRadius: 1, ...timingDialogShimmerSx }} />
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          <TimingDialogChipShimmer width={68} />
          <TimingDialogChipShimmer width={90} />
          <TimingDialogChipShimmer width={78} />
          <TimingDialogChipShimmer width={86} />
          <TimingDialogChipShimmer width={72} />
          <TimingDialogChipShimmer width={96} />
        </Stack>
        <Box sx={{ width: '36%', height: 14, borderRadius: 1, ...timingDialogShimmerSx }} />
      </Stack>
    </Stack>
    <Divider sx={{ my: 1.5 }} />
    <Stack spacing={0.75}>
      <Box sx={{ width: '55%', height: 16, borderRadius: 1, ...timingDialogShimmerSx }} />
      <Box sx={{ width: '32%', height: 16, borderRadius: 1, ...timingDialogShimmerSx }} />
    </Stack>
  </Paper>
);

const ProcessingTimelineDetailShimmer = ({ frameCount = 2 }) => {
  const count = Math.max(1, Math.min(Number(frameCount) || 2, 3));
  return (
    <Stack spacing={2}>
      <Stack spacing={0.75}>
        <Box sx={{ width: { xs: '55%', sm: '42%' }, height: 26, borderRadius: 1, ...timingDialogShimmerSx }} />
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          <TimingDialogChipShimmer width={70} />
          <TimingDialogChipShimmer width={88} />
          <TimingDialogChipShimmer width={100} />
          <TimingDialogChipShimmer width={84} />
          <TimingDialogChipShimmer width={92} />
          <TimingDialogChipShimmer width={76} />
        </Stack>
      </Stack>
      <Divider />
      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
        Captured frames
      </Typography>
      {Array.from({ length: count }).map((_, idx) => (
        <TimingDialogFrameCardShimmer key={`timing-frame-skel-${idx}`} />
      ))}
    </Stack>
  );
};

export const AuditProcessingTimelineModal = ({
  timingDialog = { open: false, log: null, stack: [] },
  closeTimingDialog = () => {},
  isMobile = false,
  timingDialogResultMeta = { label: 'Unknown', color: 'default' },
  hasTimingDialogTrackAttendance = false,
  possibleMatchTrainActiveRef = null,
  setTrainAuditId = null,
  setTrainMenuAnchor = null,
  timingDialogPossibleMatchSelectedId = null,
  timingDialogPossibleMatchManualCompanyId = '',
  setTimingDialogPossibleMatchManualCompanyId = null,
  timingDialogPossibleMatchMarkSubmitting = false,
  employees = [],
  sortedEmployees = [],
  companyOptions = [],
  employeesLoading = false,
  loadEmployees = () => {},
  openConfirmMarkAttendance = null,
  modalFrames = [],
  imageCache = {},
  ANONYMOUS_IMAGE = '/anonymous.webp',
  setTimingDialogPossibleMatchSelectedId = null,
  timingDialogPossibleMatchCandidateImages = {},
  showPossibleMatchActions = true,
  onTimingDialogRetryMatchClick = null,
  onTimingDialogRefreshTopMatchersClick = null,
  refreshTopMatchersLoading = false,
  onTimingDialogRevokeClick = null,
  canTimingDialogRevokeAttendance = false,
  timingDialogRevokeLoading = false,
  showProcessingTimeline = true,
  showTimingHeading = true,
  showTopMatches = true,
  title = 'Processing timeline',
  frameStatusLabel = null,
  frameStatusChipSx = null,
  onVisionReview = null,
  visionReviewLoading = false,
  showVisionReviewUI = false,
}) => {
  const { openImagePreview } = useImagePreview();
  const [enrolledPersonFaces, setEnrolledPersonFaces] = useState([]);
  const [enrolledPersonFacesLoading, setEnrolledPersonFacesLoading] = useState(false);
  const safeModalFrames = Array.isArray(modalFrames) ? modalFrames : [];
  const usesCustomHeaderStatusChip = Boolean(timingDialogResultMeta?.chipSx);
  const safeEmployees = Array.isArray(employees) ? employees : [];
  const safeSortedEmployees = Array.isArray(sortedEmployees) ? sortedEmployees : safeEmployees;
  const isDetailLoading = Boolean(timingDialog?.detailLoading);
  const safeCompanyOptions = Array.isArray(companyOptions) ? companyOptions : [];
  const setManualCompanyId = typeof setTimingDialogPossibleMatchManualCompanyId === 'function'
    ? setTimingDialogPossibleMatchManualCompanyId
    : () => {};
  const safeCandidateImages = timingDialogPossibleMatchCandidateImages || {};
  const buildCapturedFrameGalleryEntry = (frame, idx, frameSrc) => {
    const log = frame?.log || {};
    const auditId = log.audit_id != null ? String(log.audit_id).trim() : '';
    if (!frameSrc) return null;
    const frameLabel = log.created_at
      ? `Captured frame ${idx + 1} - ${formatDate(log.created_at, true, false)}`
      : `Captured frame ${idx + 1}`;
    return {
      auditId,
      src: frameSrc,
      title: frameLabel,
      alt: log.recognized_name || frameLabel,
    };
  };
  const capturedFrameEmployeeName =
    timingDialog.log?.recognized_name ||
    safeModalFrames.find((frame) => frame?.log?.recognized_name)?.log?.recognized_name ||
    'Unknown person';
  const capturedFrameEmployeeId =
    timingDialog.log?.employee_code ||
    timingDialog.log?.recognized_person_id ||
    safeModalFrames.find((frame) => frame?.log?.employee_code)?.log?.employee_code ||
    safeModalFrames.find((frame) => frame?.log?.recognized_person_id)?.log?.recognized_person_id ||
    '';
  const capturedFrameProfileImage = capturedFrameEmployeeId
    ? (safeCandidateImages?.[String(capturedFrameEmployeeId)] || '')
    : '';
  const openCapturedFrameGallery = async (frame) => {
    if (!frame) return;

    const clickedAuditId = frame?.log?.audit_id != null ? String(frame.log.audit_id).trim() : '';
    const galleryEntries = (
      await Promise.all(
        safeModalFrames.map(async (modalFrame, idx) => {
          const frameSrc = await resolveCapturedFramePreviewSrc(modalFrame, imageCache, ANONYMOUS_IMAGE);
          return buildCapturedFrameGalleryEntry(modalFrame, idx, frameSrc);
        }),
      )
    ).filter(Boolean);

    if (galleryEntries.length === 0) return;

    const galleryIndex = clickedAuditId
      ? galleryEntries.findIndex((item) => item.auditId === clickedAuditId)
      : -1;
    const safeIndex = galleryIndex >= 0 ? galleryIndex : 0;
    const initialImage = galleryEntries[safeIndex];
    if (!initialImage?.src) return;

    const galleryImages = galleryEntries.map(({ src, title, alt }) => ({ src, title, alt }));
    openImagePreview({
      src: initialImage.src,
      title: initialImage.title,
      alt: initialImage.alt,
      images: galleryImages,
      index: safeIndex,
      employeeName: capturedFrameEmployeeName,
      employeeId: capturedFrameEmployeeId,
      employeeImage: capturedFrameProfileImage,
    });
  };
  const isTimingDialogValidityExpired =
    String(timingDialog.log?.validity_status || '').toLowerCase() === 'expired';
  const canShowPossibleMatchActions = showPossibleMatchActions &&
    timingDialog.log?.confidence_level === 'possible_match' &&
    Array.isArray(timingDialog.log?.request_metadata?.top_candidates) &&
    timingDialog.log.request_metadata.top_candidates.length > 0 &&
    !timingDialog.log?.manual_attendance &&
    !hasTimingDialogTrackAttendance;
  const setCandidate = typeof setTimingDialogPossibleMatchSelectedId === 'function'
    ? setTimingDialogPossibleMatchSelectedId
    : () => {};
  const canShowRetryMatchAction = Boolean(timingDialog.log?.audit_id && typeof onTimingDialogRetryMatchClick === 'function');
  const canShowRefreshTopMatchersAction = Boolean(
    timingDialog.log?.audit_id &&
    timingDialog.log?.confidence_level === 'possible_match' &&
    Array.isArray(timingDialog.log?.request_metadata?.top_candidates) &&
    timingDialog.log.request_metadata.top_candidates.length > 0 &&
    typeof onTimingDialogRefreshTopMatchersClick === 'function'
  );
  const canShowRevokeAction = Boolean(canTimingDialogRevokeAttendance && typeof onTimingDialogRevokeClick === 'function');
  const canOpenPersonDetailsLink = Boolean(
    (timingDialog.log?.person_id || timingDialog.log?.recognized_person_id) && !timingDialog.log?.is_intrusion
  );
  const enrolledPersonId = resolveEnrolledPersonId(timingDialog.log);
  const canShowEnrolledPersonPhoto = Boolean(
    timingDialog.open && isMatchOrPossibleMatchLog(timingDialog.log) && enrolledPersonId
  );
  const preferredEnrolledFace = pickPreferredEnrolledFace(enrolledPersonFaces);
  const showEnrolledPersonPhoto = canShowEnrolledPersonPhoto && (
    enrolledPersonFacesLoading || Boolean(preferredEnrolledFace)
  );

  useEffect(() => {
    if (!canShowEnrolledPersonPhoto) {
      setEnrolledPersonFaces([]);
      setEnrolledPersonFacesLoading(false);
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];
    setEnrolledPersonFaces([]);
    setEnrolledPersonFacesLoading(true);

    (async () => {
      try {
        const faces = await getPersonFaces(enrolledPersonId);
        const activeFaces = (Array.isArray(faces) ? faces : []).filter((face) => (
          face?.face_id && !face.deleted_at
        ));
        const loadedFaces = (
          await Promise.all(activeFaces.map(async (face) => {
            try {
              const blob = await getFaceImage(face.face_id);
              const src = URL.createObjectURL(blob);
              objectUrls.push(src);
              return {
                face_id: face.face_id,
                face_angle: face.face_angle,
                pose_quality: face.pose_quality,
                src,
              };
            } catch {
              return null;
            }
          }))
        ).filter(Boolean);
        if (cancelled) {
          loadedFaces.forEach((face) => URL.revokeObjectURL(face.src));
          return;
        }
        setEnrolledPersonFaces(loadedFaces);
      } catch {
        if (!cancelled) setEnrolledPersonFaces([]);
      } finally {
        if (!cancelled) setEnrolledPersonFacesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [canShowEnrolledPersonPhoto, enrolledPersonId, timingDialog.log?.audit_id]);

  const openEnrolledPersonGallery = () => {
    if (!enrolledPersonFaces.length) return;
    const galleryImages = enrolledPersonFaces.map((face) => {
      const label = face.face_angle || 'Face';
      return {
        src: face.src,
        title: label,
        alt: `Face - ${label}`,
      };
    });
    const startFace = preferredEnrolledFace || enrolledPersonFaces[0];
    const startIndex = Math.max(0, enrolledPersonFaces.findIndex((face) => face.src === startFace.src));
    openImagePreview({
      src: startFace.src,
      title: startFace.face_angle || 'Face',
      alt: `Face - ${startFace.face_angle || 'enrolled'}`,
      images: galleryImages,
      index: startIndex,
      employeeName: timingDialog.log?.recognized_name || 'Unknown person',
      employeeId: timingDialog.log?.employee_code || enrolledPersonId,
      employeeImage: startFace.src,
    });
  };

  return (
    <UniversalModal
      open={timingDialog.open}
      onClose={closeTimingDialog}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      paperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
        },
      }}
      dialogProps={{ scroll: 'body' }}
      titleSx={{
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        py: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      header={
        <>
          {title}
          <IconButton onClick={closeTimingDialog} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <Close fontSize="small" />
          </IconButton>
        </>
      }
      contentDividers
      contentSx={{ overflow: 'visible', p: { xs: 2, sm: 3 } }}
      actionsSx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
      footer={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={closeTimingDialog}>Close</Button>
          {!isDetailLoading && canShowPossibleMatchActions && (
            <>
              {showVisionReviewUI && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={visionReviewLoading ? <CircularProgress size={16} color="inherit" /> : <SmartToy />}
                  disabled={visionReviewLoading || typeof onVisionReview !== 'function'}
                  onClick={() => {
                    if (typeof onVisionReview === 'function' && timingDialog.log) {
                      const force = Boolean(timingDialog.log.request_metadata?.vision_review?.reviewed);
                      onVisionReview(timingDialog.log, { source: 'timeline', force });
                    }
                  }}
                >
                  {visionReviewLoading
                    ? 'Matching…'
                    : (timingDialog.log?.request_metadata?.vision_review?.reviewed ? 'Re-run AI Matching' : 'AI Matching')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Psychology />}
                onClick={(e) => {
                  if (timingDialog.log?.audit_id && typeof setTrainAuditId === 'function' && typeof setTrainMenuAnchor === 'function') {
                    if (possibleMatchTrainActiveRef && typeof possibleMatchTrainActiveRef === 'object') {
                      possibleMatchTrainActiveRef.current = true;
                    }
                    setTrainAuditId(timingDialog.log.audit_id);
                    setTrainMenuAnchor(e.currentTarget);
                  }
                }}
              >
                Train
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!timingDialogPossibleMatchSelectedId || timingDialogPossibleMatchMarkSubmitting || typeof openConfirmMarkAttendance !== 'function'}
                onClick={() => {
                  if (typeof openConfirmMarkAttendance !== 'function') return;
                  const log = timingDialog.log;
                  const selected = safeEmployees.find((e) => e.person_id === timingDialogPossibleMatchSelectedId);
                  const personName =
                    selected?.name ||
                    log?.request_metadata?.top_candidates?.find((c) => String(c.person_id) === String(timingDialogPossibleMatchSelectedId))?.name ||
                    '';
                  openConfirmMarkAttendance('timing_dialog', personName);
                }}
              >
                {timingDialogPossibleMatchMarkSubmitting ? 'Marking…' : 'Mark attendance'}
              </Button>
            </>
          )}
        </Stack>
      }
    >
      {timingDialog.log ? (
        isDetailLoading ? (
          <ProcessingTimelineDetailShimmer
            frameCount={
              safeModalFrames.length
              || (Array.isArray(timingDialog.stack) ? timingDialog.stack.length : 0)
              || Number(timingDialog.log?.stack_size)
              || 2
            }
          />
        ) : (
        <Stack spacing={{ xs: 2, sm: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {showEnrolledPersonPhoto && (
              <Box
                data-no-image-preview="true"
                onClick={preferredEnrolledFace ? openEnrolledPersonGallery : undefined}
                sx={{
                  width: { xs: 72, sm: 96 },
                  height: { xs: 72, sm: 96 },
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  background: '#0f172a',
                  flexShrink: 0,
                  cursor: preferredEnrolledFace ? 'pointer' : 'default',
                }}
              >
                {preferredEnrolledFace ? (
                  <Box
                    component="img"
                    src={preferredEnrolledFace.src}
                    alt={timingDialog.log.recognized_name || 'Enrolled person'}
                    data-no-image-preview="true"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : null}
              </Box>
            )}
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                <Box
                  component="span"
                  onClick={canOpenPersonDetailsLink ? () => closeTimingDialog() : undefined}
                >
                  <EmployeeNameHyperlink
                    text={timingDialog.log.recognized_name || 'Unknown person'}
                    employees={[buildPersonLinkMeta(timingDialog.log, { routeSource: 'event' })]}
                    disabled={!canOpenPersonDetailsLink}
                    style={{ display: 'inline' }}
                  />
                </Box>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5, minWidth: 0, flex: 1 }}>
                  <Chip
                    size="small"
                    label={timingDialogResultMeta.label}
                    color={
                      usesCustomHeaderStatusChip
                        ? 'default'
                        : (timingDialogResultMeta.color === 'default' ? 'default' : timingDialogResultMeta.color)
                    }
                    variant={
                      usesCustomHeaderStatusChip
                        ? 'filled'
                        : (timingDialogResultMeta.color === 'default' ? 'outlined' : 'filled')
                    }
                    sx={{
                      height: { xs: 22, sm: 24 },
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      ...(timingDialogResultMeta.chipSx || {}),
                    }}
                  />
                  {isTimingDialogValidityExpired && (
                    <Chip
                      size="small"
                      label="Validity Expired"
                      variant="filled"
                      sx={{
                        height: { xs: 22, sm: 24 },
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        bgcolor: '#d32f2f',
                        color: '#fff',
                      }}
                    />
                  )}
                  {(timingDialog.log.employee_code || timingDialog.log.recognized_person_id) && (
                    <Chip
                      size="small"
                      label={`ID: ${timingDialog.log.employee_code || timingDialog.log.recognized_person_id}`}
                      variant="outlined"
                      sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {timingDialog.log.track_id != null && (
                    <Chip
                      size="small"
                      label={`Track: ${timingDialog.log.track_id}`}
                      variant="outlined"
                      sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {isAttendanceSent(timingDialog.log?.send_attendance) && (
                    <Chip
                      size="small"
                      label="Attendance"
                      color="success"
                      variant="filled"
                      sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {Boolean(timingDialog.log?.is_violation) && (
                    <Chip
                      size="small"
                      label="Violation"
                      variant="filled"
                      sx={{
                        height: { xs: 22, sm: 24 },
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        bgcolor: '#fee2e2',
                        color: '#b91c1c',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {timingDialog.log.camera && (
                    <Chip
                      size="small"
                      label={`Camera: ${(timingDialog.log.camera || '').toUpperCase()}`}
                      variant="outlined"
                      sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {timingDialog.log.operation_type && (
                    <Chip
                      size="small"
                      label={timingDialog.log.operation_type}
                      variant="outlined"
                      sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                </Stack>

                {(canShowRevokeAction || canShowRetryMatchAction || canShowRefreshTopMatchersAction) && (
                  <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, pt: 0.1 }}>
                    {canShowRevokeAction && (
                      <Tooltip title={timingDialogRevokeLoading ? 'Revoking...' : 'Revoke Attendance'} arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={timingDialogRevokeLoading || refreshTopMatchersLoading}
                            sx={{
                              bgcolor: 'rgba(229, 229, 229, 0.95)',
                              color: '#dc2626',
                              width: 30,
                              height: 30,
                              borderRadius: 2,
                              '&:hover': {
                                bgcolor: 'rgba(229, 229, 229, 0.95)',
                                color: '#b91c1c',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTimingDialogRevokeClick(e);
                            }}
                          >
                            <BlockIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canShowRetryMatchAction && (
                      <Tooltip title="Retry Match" arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={refreshTopMatchersLoading}
                            sx={{
                              bgcolor: 'rgba(229, 229, 229, 0.95)',
                              color: '#2563eb',
                              width: 30,
                              height: 30,
                              borderRadius: 2,
                              '&:hover': {
                                bgcolor: 'rgba(229, 229, 229, 0.95)',
                                color: '#1d4ed8',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTimingDialogRetryMatchClick(e);
                            }}
                          >
                            <RestartAltIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canShowRefreshTopMatchersAction && (
                      <Tooltip title={refreshTopMatchersLoading ? 'Refreshing top matchers…' : 'Refresh Top Matchers'} arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={refreshTopMatchersLoading}
                            aria-label="Refresh Top Matchers"
                            sx={{
                              bgcolor: 'rgba(229, 229, 229, 0.95)',
                              color: '#0f766e',
                              width: 30,
                              height: 30,
                              borderRadius: 2,
                              '&:hover': {
                                bgcolor: 'rgba(229, 229, 229, 0.95)',
                                color: '#0d9488',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTimingDialogRefreshTopMatchersClick(e);
                            }}
                          >
                            {refreshTopMatchersLoading
                              ? <CircularProgress size={14} color="inherit" />
                              : <RefreshIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Stack>
          <Divider />
          {timingDialog.log && isAttendanceRevokedOnLog(timingDialog.log) && getAttendanceRevokedReopenHint(timingDialog.log) && (
            <Alert severity="info" sx={{ my: 2 }}>
              {getAttendanceRevokedReopenHint(timingDialog.log)}
            </Alert>
          )}
          {canShowPossibleMatchActions && (
            (() => {
              const candidates = timingDialog.log.request_metadata.top_candidates;
              const displayCandidates = [...candidates.slice(0, 3), null, null, null].slice(0, 3);
              const n = 3;
              const matchersHeaderHeight = 28;
              const matcherCardMinHeight = 100;
              const matcherCardGap = 10;
              const matcherListHeight = (n * matcherCardMinHeight) + ((n - 1) * matcherCardGap);
              const timingDialogPrimaryFrame = timingDialog.log?.audit_id
                ? safeModalFrames.find((frame) => String(frame?.log?.audit_id || '') === String(timingDialog.log.audit_id))
                : null;
              const timingDialogCompositeFrame =
                timingDialogPrimaryFrame ||
                safeModalFrames[0] ||
                (timingDialog.log ? { log: timingDialog.log, image: null } : null);
              const timingDialogImage = timingDialogCompositeFrame
                ? resolveProcessingTimelineFrameSrc(timingDialogCompositeFrame, imageCache, ANONYMOUS_IMAGE)
                : ANONYMOUS_IMAGE;
              const topCandidateIds = new Set(
                displayCandidates
                  .filter(Boolean)
                  .map((candidate) => String(candidate.person_id).trim())
                  .filter(Boolean)
              );
              return (
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {showVisionReviewUI && (
                    <VisionReviewRecommendation
                      visionReview={timingDialog.log?.request_metadata?.vision_review}
                      topCandidates={timingDialog.log?.request_metadata?.top_candidates}
                    />
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    Possible match — select person and mark attendance
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0, minHeight: 280 }}>
                    <Box sx={{ flex: '0 0 200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Current capture
                      </Typography>
                      <Box sx={{ width: 160, height: 160, borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.200', border: '1px solid', borderColor: 'divider' }}>
                        <Box component="img" src={timingDialogImage} alt="Current capture" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    </Box>
                    <Box sx={{ flex: '0 0 72px', position: 'relative', minWidth: 72, mt: `${matchersHeaderHeight}px`, height: `${matcherListHeight}px` }}>
                      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
                        {displayCandidates.map((_, i) => {
                          const yCenterPx = (matcherCardMinHeight / 2) + (i * (matcherCardMinHeight + matcherCardGap));
                          const yEnd = (yCenterPx / matcherListHeight) * 100;
                          const yStart = 50;
                          const cx = 50;
                          const path = `M 0 ${yStart} Q ${cx} ${yStart}, ${cx} ${yEnd} Q ${cx} ${yEnd}, 100 ${yEnd}`;
                          return <path key={i} d={path} fill="none" stroke="#9e9e9e" strokeWidth="1.2" strokeLinecap="round" />;
                        })}
                      </svg>
                    </Box>
                    <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', gap: 0.5, minWidth: 0, maxWidth: 220 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ height: `${matchersHeaderHeight}px`, display: 'flex', alignItems: 'center' }}>
                        Top matchers
                      </Typography>
                      <RadioGroup
                        value={timingDialogPossibleMatchSelectedId || ''}
                        onChange={(e) => setCandidate(e.target.value || null)}
                        sx={{ display: 'flex', flexDirection: 'column', gap: `${matcherCardGap}px`, minHeight: `${matcherListHeight}px` }}
                      >
                        {displayCandidates.map((candidate, idx) => {
                          const isPlaceholder = candidate == null;
                          const pid = candidate?.person_id ? String(candidate.person_id).trim() : null;
                          const candidateImage = pid ? safeCandidateImages[pid] : null;
                          return (
                            <Paper
                              key={pid != null ? pid : `placeholder-${idx}`}
                              variant="outlined"
                              sx={{
                                p: 0.75,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 0.5,
                                cursor: isPlaceholder ? 'default' : 'pointer',
                                opacity: isPlaceholder ? 0.7 : 1,
                                minHeight: `${matcherCardMinHeight}px`,
                                border: !isPlaceholder && timingDialogPossibleMatchSelectedId === pid ? '2px solid' : '1px solid',
                                borderColor: !isPlaceholder && timingDialogPossibleMatchSelectedId === pid ? 'primary.main' : 'divider',
                                bgcolor: !isPlaceholder && timingDialogPossibleMatchSelectedId === pid ? 'action.selected' : 'background.paper',
                              }}
                              onClick={isPlaceholder ? undefined : () => setCandidate(pid || null)}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                                <Box sx={{ width: 72, height: 88, flexShrink: 0, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.300' }}>
                                  <Box
                                    component="img"
                                    src={candidateImage || ANONYMOUS_IMAGE}
                                    alt={isPlaceholder ? '—' : (candidate.name || 'Candidate')}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </Box>
                                <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600} noWrap>
                                    {isPlaceholder ? 'No other candidate' : (candidate.name || 'Unknown')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {isPlaceholder ? '—' : `Matching score: ${Number(candidate.score).toFixed(2)}`}
                                  </Typography>
                                </Box>
                              </Box>
                              {isPlaceholder ? (
                                <Radio size="small" disabled sx={{ p: 0.25, flexShrink: 0 }} />
                              ) : (
                                <Radio size="small" value={pid || ''} sx={{ p: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                              )}
                            </Paper>
                          );
                        })}
                      </RadioGroup>
                    </Box>
                  </Box>
                  <PossibleMatchManualPersonSelect
                    companyOptions={safeCompanyOptions}
                    manualCompanyId={timingDialogPossibleMatchManualCompanyId}
                    setManualCompanyId={setManualCompanyId}
                    selectedPersonId={timingDialogPossibleMatchSelectedId}
                    setSelectedPersonId={setCandidate}
                    sortedEmployees={safeSortedEmployees}
                    employeesLoading={employeesLoading}
                    loadEmployees={loadEmployees}
                    topCandidateIds={topCandidateIds}
                    sessionKey={timingDialog.log?.audit_id || ''}
                  />
                </Stack>
              );
            })()
          )}
          {safeModalFrames.length > 0 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                Captured frames
              </Typography>
              {safeModalFrames.map((frame, idx) => (
              <ProcessingTimelineFrameDetailsCard
                key={frame.log.audit_id || `frame-${idx}`}
                frame={frame}
                imageCache={imageCache}
                ANONYMOUS_IMAGE={ANONYMOUS_IMAGE}
                showProcessingTimeline={showProcessingTimeline}
                showTimingHeading={showTimingHeading}
                showTopMatches={showTopMatches}
                statusLabel={frameStatusLabel}
                statusChipSx={frameStatusChipSx}
                onFrameImageClick={() => openCapturedFrameGallery(frame)}
              />
            ))}
          </Stack>
          )}
        </Stack>
        )
      ) : (
        <Typography color="text.secondary">Select a tile to view timing data.</Typography>
      )}
    </UniversalModal>
  );
};

const AuditDialogs = ({
  findPersonSearchIconAuditLogsModalOpen,
  handleCloseFindPersonSearchIconAuditLogs,
  handleSearchFindPersonSearchIconAuditLogs,
  findPersonSearchIconAuditLogsLoading,
  findPersonSearchIconAuditLogsSelectedImage,
  primary,
  findPersonSearchIconAuditLogsStackImages,
  findPersonSearchIconAuditLogsSelectedAuditId,
  getCapturedTimestamp,
  setFindPersonSearchIconAuditLogsSelectedImage,
  setFindPersonSearchIconAuditLogsSelectedAuditId,
  findPersonSearchIconAuditLogsThreshold,
  setFindPersonSearchIconAuditLogsThreshold,
  attendanceExportDialogOpen,
  setAttendanceExportDialogOpen,
  handleExportAttendance,
  exportAttendanceLoading,
  attendanceExportFormat = 'csv',
  attendanceExportSelectedFields,
  attendanceExportFileName,
  setAttendanceExportFileName,
  setAttendanceExportDatePickerAnchor,
  attendanceExportDateRange,
  attendanceExportStartTime,
  attendanceExportEndTime,
  attendanceExportDatePickerOpen,
  attendanceExportDatePickerAnchor,
  setAttendanceExportDateRange,
  isMobile,
  setAttendanceExportStartTime,
  setAttendanceExportEndTime,
  buildExportDateTimeRange,
  show,
  availableNames,
  attendanceExportPersonName,
  attendanceExportAllEmployeesPresence,
  setAttendanceExportPersonName,
  setAttendanceExportAllEmployeesPresence,
  setAttendanceExportSelectedFields,
  attendanceExportAction,
  setAttendanceExportAction,
  attendanceExportPremisesFilter,
  setAttendanceExportPremisesFilter,
  attendanceExportLateFilter,
  setAttendanceExportLateFilter,
  lateEntryTrackingEnabled = true,
  attendanceExportVisibleFields,
  moveSelectedField,
  toggleAttendanceExportField,
  snackbar,
  setSnackbar,
  timingDialog,
  closeTimingDialog,
  timingDialogResultMeta,
  hasTimingDialogTrackAttendance,
  possibleMatchTrainActiveRef,
  setTrainAuditId,
  setTrainMenuAnchor,
  timingDialogPossibleMatchSelectedId,
  timingDialogPossibleMatchMarkSubmitting,
  employees,
  sortedEmployees,
  openConfirmMarkAttendance,
  modalFrames,
  imageCache,
  ANONYMOUS_IMAGE,
  setTimingDialogPossibleMatchSelectedId,
  timingDialogPossibleMatchCandidateImages,
  onTimingDialogRetryMatchClick,
  onTimingDialogRefreshTopMatchersClick = null,
  refreshTopMatchersLoading = false,
  onTimingDialogRevokeClick,
  canTimingDialogRevokeAttendance,
  timingDialogRevokeLoading,
  addToEmployeeDialog,
  setAddToEmployeeDialog,
  handleAddToEmployee,
  operationLoading,
  selectedPersonId,
  companyOptions,
  employeeCompanyId,
  setEmployeeCompanyId,
  setSelectedPersonId,
  setSelectedName,
  setSelectedEmployeeCode,
  setSelectedCompanyId,
  setSelectedBranchId,
  OTHER_COMPANY_OPTION_VALUE,
  employeesLoading,
  presentDialogOpen,
  handleClosePresentDialog,
  handlePresentSubmit,
  presentSubmitting,
  presentPersonId,
  setPresentPersonId,
  setPresentName,
  markAttendanceDialog,
  handleCloseMarkAttendance,
  handleMarkAttendanceSubmit,
  markAttendanceSubmitting,
  markAttendancePersonId,
  markAttendancePersonName,
  markAttendanceCheckIn,
  markAttendanceCheckOut,
  markAttendanceRemark,
  markAttendanceCompanyId,
  setMarkAttendanceCompanyId,
  setMarkAttendancePersonId,
  setMarkAttendancePersonName,
  loadEmployees,
  calendarType,
  setCalendarType,
  setSelectedAttendanceDate,
  setAttendanceCalendarOpen,
  setMarkAttendanceRemark,
  attendanceCalendarOpen,
  selectedAttendanceDate,
  setMarkAttendanceCheckIn,
  setMarkAttendanceCheckOut,
  markAttendanceContext = null,
  possibleMatchModal,
  closePossibleMatchModal,
  setPossibleMatchSelectedId,
  possibleMatchSelectedId,
  possibleMatchManualCompanyId,
  setPossibleMatchManualCompanyId,
  timingDialogPossibleMatchManualCompanyId,
  setTimingDialogPossibleMatchManualCompanyId,
  possibleMatchMarkSubmitting,
  possibleMatchCandidateImages,
  confirmMarkAttendanceOpen,
  closeConfirmMarkAttendance,
  handleConfirmMarkAttendanceSubmit,
  confirmMarkAttendanceSource,
  confirmMarkAttendancePersonName,
  trainMenuAnchor,
  handleTrainClose,
  handleOpenAddToEmployee,
  handleOpenCreateEmployee,
  handleOpenCreateEmployeeProfile,
  handleOpenCreateVendor,
  handleOpenCreateIntrusion,
  handleOpenCreateVisitor,
  vendorManagementEnabled = false,
  visitorManagementEnabled = false,
  createProfileTypeDialogOpen,
  setCreateProfileTypeDialogOpen,
  previewOpen,
  closePreview,
  previewUrl,
  bucketManualMarkDialog,
  handleCloseBucketManualMark,
  handleBucketManualMarkRequestConfirm,
  handleBucketManualMarkSubmit,
  bucketManualMarkSubmitting,
  bucketManualMarkRemark,
  setBucketManualMarkRemark,
  bucketManualMarkPersonId,
  bucketManualMarkPersonName,
  bucketManualMarkEmployeeCode,
  bucketManualMarkSelectedAuditId,
  setBucketManualMarkSelectedAuditId,
  onVisionReview = null,
  visionReviewLoading = false,
  showVisionReviewUI = false,
}) => {
  const isMissingEntryMarkMode = markAttendanceContext?.source === 'missing_entry';
  const constrainedAction = isMissingEntryMarkMode
    ? (markAttendanceContext?.allowedAction === 'check_in' ? 'check_in' : 'check_out')
    : null;
  const showOnlyCheckIn = constrainedAction === 'check_in';
  const showOnlyCheckOut = constrainedAction === 'check_out';
  const disableCompanySelect = Boolean(isMissingEntryMarkMode && markAttendanceContext?.lockCompany);
  const disableEmployeeSelect = Boolean(isMissingEntryMarkMode && markAttendanceContext?.lockEmployee);
  const constraintStart = markAttendanceContext?.windowStart ? new Date(markAttendanceContext.windowStart) : null;
  const constraintEnd = markAttendanceContext?.windowEnd ? new Date(markAttendanceContext.windowEnd) : null;
  const hasConstraintRange = Boolean(
    constraintStart &&
    !Number.isNaN(constraintStart.getTime()) &&
    constraintEnd &&
    !Number.isNaN(constraintEnd.getTime())
  );
  const now = new Date();
  const calendarMaxDate = hasConstraintRange
    ? (constraintEnd < now ? constraintEnd : now)
    : now;
  const isInConstraintRange = (dateValue) => {
    if (!hasConstraintRange || !(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
      return true;
    }
    return dateValue >= constraintStart && dateValue <= constraintEnd;
  };
  const getMissingEntryDefaultSelection = (type) => {
    if (!hasConstraintRange) return new Date();
    if (type === 'checkout') {
      return new Date(constraintEnd);
    }
    return new Date(constraintStart);
  };
  return (
    <>
      <FindPersonSearchFromTileModal
        open={findPersonSearchIconAuditLogsModalOpen}
        onClose={handleCloseFindPersonSearchIconAuditLogs}
        onSearch={handleSearchFindPersonSearchIconAuditLogs}
        loading={findPersonSearchIconAuditLogsLoading}
        selectedImage={findPersonSearchIconAuditLogsSelectedImage}
        primary={primary}
        stackImages={findPersonSearchIconAuditLogsStackImages}
        selectedItemId={findPersonSearchIconAuditLogsSelectedAuditId}
        onSelectItem={(item) => {
          setFindPersonSearchIconAuditLogsSelectedImage(item.image);
          setFindPersonSearchIconAuditLogsSelectedAuditId(item.uniqueId);
        }}
        getCapturedTimestamp={getCapturedTimestamp}
        threshold={findPersonSearchIconAuditLogsThreshold}
        onThresholdChange={setFindPersonSearchIconAuditLogsThreshold}
        introText="Select an image from the bucket/stack to use for Find Person search. The search will display results in the main audit logs view."
      />

      <UniversalModal
        open={attendanceExportDialogOpen}
        onClose={() => setAttendanceExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        title={
          <Typography component="div" variant="inherit" sx={{ fontWeight: 600, minWidth: 0 }}>
            Export Attendance {String(attendanceExportFormat || 'csv').toUpperCase()}
          </Typography>
        }
        headerRight={
          <IconButton
            onClick={() => setAttendanceExportDialogOpen(false)}
            size="small"
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
        titleSx={{
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          py: { xs: 1.5, sm: 2 },
        }}
        contentDividers
        footer={
          <>
            <Button onClick={() => setAttendanceExportDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleExportAttendance}
              disabled={exportAttendanceLoading || attendanceExportSelectedFields.length === 0}
            >
              {exportAttendanceLoading ? 'Exporting...' : `Export ${String(attendanceExportFormat || 'csv').toUpperCase()}`}
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
            <TextField
              label="File name"
              size="small"
              value={attendanceExportFileName}
              onChange={(e) => setAttendanceExportFileName(e.target.value)}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                Timestamp Date Filter
              </Typography>
              <Box
                onClick={(e) => setAttendanceExportDatePickerAnchor(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  px: 1.2,
                  py: 0.95,
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
                }}
              >
                <CalendarMonth fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                  {isSameDay(attendanceExportDateRange[0].startDate, attendanceExportDateRange[0].endDate)
                    ? format(attendanceExportDateRange[0].startDate, 'MMM dd, yyyy')
                    : `${format(attendanceExportDateRange[0].startDate, 'MMM dd')} - ${format(attendanceExportDateRange[0].endDate, 'MMM dd, yyyy')}`}
                  {`  (${attendanceExportStartTime} - ${attendanceExportEndTime})`}
                </Typography>
              </Box>
              <Popover
                open={attendanceExportDatePickerOpen}
                anchorEl={attendanceExportDatePickerAnchor}
                onClose={() => setAttendanceExportDatePickerAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: { maxWidth: '100vw', overflow: 'auto' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box sx={{
                    p: 2,
                    borderRight: { md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: 'divider',
                    minWidth: 140,
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Quick Select
                    </Typography>
                    <Stack spacing={0.5}>
                      {[
                        { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                        { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                        { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                        { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                        { label: 'This Month', getValue: () => {
                          const now = new Date();
                          return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                        }},
                        { label: 'Last Month', getValue: () => {
                          const now = new Date();
                          return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                        }},
                      ].map((preset) => (
                        <Button
                          key={preset.label}
                          size="small"
                          variant="text"
                          onClick={() => {
                            const { startDate, endDate } = preset.getValue();
                            setAttendanceExportDateRange([{ startDate, endDate, key: 'selection' }]);
                          }}
                          sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.75,
                            color: 'text.primary',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>

                  <Box sx={{
                    '& .rdrCalendarWrapper': {
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    },
                    '& .rdrMonth': {
                      width: { xs: '280px', sm: '300px' },
                    },
                  }}>
                    <DateRangePicker
                      ranges={attendanceExportDateRange}
                      onChange={(ranges) => setAttendanceExportDateRange([ranges.selection])}
                      moveRangeOnFirstSelection={false}
                      months={isMobile ? 1 : 2}
                      direction={isMobile ? 'vertical' : 'horizontal'}
                      rangeColors={[primary]}
                      staticRanges={[]}
                      inputRanges={[]}
                      maxDate={new Date()}
                    />
                    <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="Start Time"
                        type="time"
                        size="small"
                        value={attendanceExportStartTime}
                        onChange={(e) => setAttendanceExportStartTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        fullWidth
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        size="small"
                        value={attendanceExportEndTime}
                        onChange={(e) => setAttendanceExportEndTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                      <Button
                        onClick={() => {
                          const today = new Date();
                          setAttendanceExportDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                          setAttendanceExportStartTime('00:00');
                          setAttendanceExportEndTime('23:59');
                        }}
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                      >
                        Reset
                      </Button>
                      <Button onClick={() => setAttendanceExportDatePickerAnchor(null)} variant="outlined" size="small">
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          const range = buildExportDateTimeRange();
                          if (range?.error) {
                            show(range.error, 'error');
                            return;
                          }
                          setAttendanceExportDatePickerAnchor(null);
                        }}
                        variant="contained"
                        size="small"
                      >
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Popover>
            </Box>

            <Autocomplete
              size="small"
              freeSolo
              options={availableNames}
              value={attendanceExportPersonName || null}
              inputValue={attendanceExportPersonName || ''}
              disabled={attendanceExportAllEmployeesPresence}
              onInputChange={(e, newValue) => setAttendanceExportPersonName(sanitizeNameSearchInput(newValue || ''))}
              renderInput={(params) => <TextField {...params} label="Search by Name" />}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={attendanceExportAllEmployeesPresence}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAttendanceExportAllEmployeesPresence(checked);
                    setAttendanceExportSelectedFields((prev) => {
                      if (checked) {
                        return prev.includes('presence_time') ? prev : [...prev, 'presence_time'];
                      }
                      return prev.filter((field) => field !== 'presence_time');
                    });
                  }}
                />
              }
              label="Presence Time (Duration)"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id="export-action-label" shrink>
                  Select Action
                </InputLabel>
                <Select
                  labelId="export-action-label"
                  value={attendanceExportAction}
                  label="Select Action"
                  onChange={(e) => setAttendanceExportAction(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v ? (v === 'check_in' ? 'Check-In' : v === 'check_out' ? 'Check-Out' : 'All') : 'All'}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="check_in">Check-In</MenuItem>
                  <MenuItem value="check_out">Check-Out</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="export-premises-label" shrink>
                  Premises Status
                </InputLabel>
                <Select
                  labelId="export-premises-label"
                  value={attendanceExportPremisesFilter}
                  label="Premises Status"
                  onChange={(e) => setAttendanceExportPremisesFilter(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v ? (v === 'in' ? 'In Premises' : 'Out of Premises') : 'All Status'}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="in">In Premises</MenuItem>
                  <MenuItem value="out">Out of Premises</MenuItem>
                </Select>
              </FormControl>

              {lateEntryTrackingEnabled && (
                <FormControl size="small" fullWidth>
                  <InputLabel id="export-late-filter-label" shrink>
                    Late Filter
                  </InputLabel>
                  <Select
                    labelId="export-late-filter-label"
                    value={attendanceExportLateFilter ? 'late' : ''}
                    label="Late Filter"
                    onChange={(e) => setAttendanceExportLateFilter(e.target.value === 'late')}
                    displayEmpty
                    renderValue={(v) => v === 'late' ? 'Late Entries' : 'All Entries'}
                  >
                    <MenuItem value="">All Entries</MenuItem>
                    <MenuItem value="late">Late Entries</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>

            <Divider />

            <Typography variant="subtitle2">Columns (select + order)</Typography>
            <List dense sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {(() => {
                const fieldByName = new Map(attendanceExportVisibleFields.map((field) => [field.field, field]));
                // First render selected fields in their manual order (so arrows work visually)
                const selectedFieldsInOrder = attendanceExportSelectedFields
                  .map((fieldName) => fieldByName.get(fieldName))
                  .filter(Boolean);
                // Then render unselected fields in config order
                const unselectedFields = attendanceExportVisibleFields.filter(
                  (field) => !attendanceExportSelectedFields.includes(field.field)
                );
                const orderedFields = [...selectedFieldsInOrder, ...unselectedFields];

                return orderedFields.map((field) => {
                  const selected = attendanceExportSelectedFields.includes(field.field);
                  const selectedIndex = attendanceExportSelectedFields.indexOf(field.field);
                  return (
                    <ListItem
                      key={field.field}
                      secondaryAction={selected ? (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => moveSelectedField(selectedIndex, 'up')} disabled={selectedIndex <= 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveSelectedField(selectedIndex, 'down')}
                            disabled={selectedIndex < 0 || selectedIndex >= attendanceExportSelectedFields.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : null}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={selected}
                          onChange={(e) => toggleAttendanceExportField(field.field, e.target.checked)}
                        />
                      </ListItemIcon>
                      <ListItemText primary={field.label || field.field} />
                    </ListItem>
                  );
                });
              })()}
            </List>
          </Stack>
      </UniversalModal>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>

      <AuditProcessingTimelineModal
        timingDialog={timingDialog}
        closeTimingDialog={closeTimingDialog}
        isMobile={isMobile}
        timingDialogResultMeta={timingDialogResultMeta}
        hasTimingDialogTrackAttendance={hasTimingDialogTrackAttendance}
        possibleMatchTrainActiveRef={possibleMatchTrainActiveRef}
        setTrainAuditId={setTrainAuditId}
        setTrainMenuAnchor={setTrainMenuAnchor}
        timingDialogPossibleMatchSelectedId={timingDialogPossibleMatchSelectedId}
        timingDialogPossibleMatchManualCompanyId={timingDialogPossibleMatchManualCompanyId}
        setTimingDialogPossibleMatchManualCompanyId={setTimingDialogPossibleMatchManualCompanyId}
        timingDialogPossibleMatchMarkSubmitting={timingDialogPossibleMatchMarkSubmitting}
        employees={employees}
        sortedEmployees={sortedEmployees}
        companyOptions={companyOptions}
        employeesLoading={employeesLoading}
        loadEmployees={loadEmployees}
        openConfirmMarkAttendance={openConfirmMarkAttendance}
        modalFrames={modalFrames}
        imageCache={imageCache}
        ANONYMOUS_IMAGE={ANONYMOUS_IMAGE}
        setTimingDialogPossibleMatchSelectedId={setTimingDialogPossibleMatchSelectedId}
        timingDialogPossibleMatchCandidateImages={timingDialogPossibleMatchCandidateImages}
        showPossibleMatchActions
        onTimingDialogRetryMatchClick={onTimingDialogRetryMatchClick}
        onTimingDialogRefreshTopMatchersClick={onTimingDialogRefreshTopMatchersClick}
        refreshTopMatchersLoading={refreshTopMatchersLoading}
        onTimingDialogRevokeClick={onTimingDialogRevokeClick}
        canTimingDialogRevokeAttendance={canTimingDialogRevokeAttendance}
        timingDialogRevokeLoading={timingDialogRevokeLoading}
        onVisionReview={onVisionReview}
        visionReviewLoading={visionReviewLoading}
        showVisionReviewUI={showVisionReviewUI}
      />

      {/* Add to Employee Dialog */}
      <UniversalModal
        open={addToEmployeeDialog}
        onClose={() => setAddToEmployeeDialog(false)}
        maxWidth="sm"
        fullWidth
        title="Add Image to Existing Employee"
        footer={
          <>
            <Button onClick={() => setAddToEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={handleAddToEmployee} variant="contained" disabled={operationLoading || !selectedPersonId}>
              {operationLoading ? 'Adding...' : 'Add Image'}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {companyOptions.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Company</InputLabel>
              <Select
                value={employeeCompanyId}
                label="Company"
                onChange={(e) => {
                  setEmployeeCompanyId(e.target.value);
                  setSelectedPersonId('');
                  setSelectedName('');
                  setSelectedEmployeeCode('');
                  setSelectedCompanyId('');
                  setSelectedBranchId('');
                }}
              >
                {companyOptions.map((cfg) => (
                  <MenuItem key={cfg.config_id || cfg.company_id || 'default'} value={cfg.company_id || ''}>
                    {cfg.company_name || 'Default Company'}
                  </MenuItem>
                ))}
                <MenuItem value={OTHER_COMPANY_OPTION_VALUE}>Other</MenuItem>
              </Select>
            </FormControl>
          )}
          <Autocomplete
            options={employees}
            loading={employeesLoading}
            filterOptions={filterEmployeePickerOptions}
            getOptionLabel={(option) => {
              const code = option?.employee_code ? ` (${option.employee_code})` : '';
              return `${option?.name || 'Unknown'}${code}`;
            }}
            value={employees.find(e => e.person_id === selectedPersonId) || null}
            onChange={(e, newValue) => {
              if (newValue) {
                setSelectedPersonId(newValue.person_id);
                setSelectedName(newValue.name);
                setSelectedEmployeeCode(newValue.employee_code);
                setSelectedCompanyId(newValue.company_id || '');
                setSelectedBranchId(newValue.branch_id || '');
              } else {
                setSelectedPersonId('');
                setSelectedName('');
                setSelectedEmployeeCode('');
                setSelectedCompanyId('');
                setSelectedBranchId('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employee"
                placeholder="Search by name, ID, or Employee Code"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {employeesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </UniversalModal>

      {/* Manual Present Dialog */}
      <UniversalModal
        open={presentDialogOpen}
        onClose={handleClosePresentDialog}
        maxWidth="sm"
        fullWidth
        title="Mark Present"
        footer={
          <>
            <Button onClick={handleClosePresentDialog}>Cancel</Button>
            <Button
              onClick={handlePresentSubmit}
              variant="contained"
              disabled={presentSubmitting || !presentPersonId}
            >
              {presentSubmitting ? 'Saving...' : 'Mark Present'}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select the employee. The attendance entry will use the audit capture time.
            </Typography>
            {companyOptions.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={employeeCompanyId}
                  label="Company"
                  onChange={(e) => {
                    setEmployeeCompanyId(e.target.value);
                    setPresentPersonId('');
                    setPresentName('');
                  }}
                >
                  {companyOptions.map((cfg) => (
                    <MenuItem key={cfg.config_id || cfg.company_id || 'default-present'} value={cfg.company_id || ''}>
                      {cfg.company_name || 'Default Company'} {cfg.company_id ? `(${cfg.company_id})` : ''}
                    </MenuItem>
                  ))}
                 <MenuItem value={OTHER_COMPANY_OPTION_VALUE}>Other</MenuItem>
                </Select>
              </FormControl>
            )}
            <Autocomplete
              options={employees}
              loading={employeesLoading}
              filterOptions={filterEmployeePickerOptions}
              getOptionLabel={(option) => {
                const code = option?.employee_code ? ` (${option.employee_code})` : '';
                return `${option?.name || 'Unknown'}${code}`;
              }}
              value={employees.find(e => e.person_id === presentPersonId) || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  setPresentPersonId(newValue.person_id);
                  setPresentName(newValue.name);
                } else {
                  setPresentPersonId('');
                  setPresentName('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  placeholder="Search by name, ID, or Employee Code"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {employeesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
        </Box>
      </UniversalModal>

      {/* Bucket manual mark attendance (audit log tile) — same layout as Find Person image picker */}
      {(() => {
        const topLog = bucketManualMarkDialog?.topLog;
        const bucketFrames = topLog ? (
          Array.isArray(bucketManualMarkDialog.stackImages) && bucketManualMarkDialog.stackImages.length > 0
            ? bucketManualMarkDialog.stackImages
            : [{ log: topLog, image: null }]
        ) : [];
        const frameItems = bucketFrames
          .map((frame, idx) => {
            const frameLog = frame?.log || topLog;
            const auditId = frameLog?.audit_id ? String(frameLog.audit_id) : '';
            if (!auditId) return null;
            const thumbSrc = frame?.image || imageCache?.[auditId] || null;
            return {
              log: frameLog,
              image: thumbSrc,
              isMain: auditId === String(topLog?.audit_id || ''),
              uniqueId: auditId,
              idx,
            };
          })
          .filter(Boolean);
        const selectedId = String(
          bucketManualMarkSelectedAuditId || topLog?.audit_id || frameItems[0]?.uniqueId || ''
        );
        const employeeDisplay = topLog
          ? (() => {
              const name = bucketManualMarkPersonName || topLog.recognized_name || 'Unknown';
              const code = bucketManualMarkEmployeeCode || topLog.employee_code || '';
              return code ? `${name} (${code})` : name;
            })()
          : '';

        return (
          <UniversalModal
            open={Boolean(bucketManualMarkDialog?.open)}
            onClose={handleCloseBucketManualMark}
            maxWidth="md"
            fullWidth
            header={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Mark Attendance Manually</Typography>
                <IconButton
                  onClick={handleCloseBucketManualMark}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                  disabled={bucketManualMarkSubmitting}
                >
                  <Close />
                </IconButton>
              </Box>
            }
            contentDividers
            footer={
              <>
                <Button
                  onClick={handleCloseBucketManualMark}
                  disabled={bucketManualMarkSubmitting}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleBucketManualMarkRequestConfirm}
                  disabled={
                    bucketManualMarkSubmitting ||
                    !bucketManualMarkPersonId ||
                    !bucketManualMarkSelectedAuditId ||
                    !bucketManualMarkRemark?.trim() ||
                    typeof openConfirmMarkAttendance !== 'function'
                  }
                  startIcon={<HowToReg />}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: primary,
                    '&:hover': {
                      backgroundColor: primary,
                      opacity: 0.9,
                    },
                  }}
                >
                  {bucketManualMarkSubmitting ? 'Marking…' : 'Mark Attendance'}
                </Button>
              </>
            }
          >
            {topLog ? (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Select a capture from this bucket, add a remark, then mark attendance. The selected capture
                timestamp will be used.
              </Typography>

              {frameItems.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Select Image ({frameItems.length} available)
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                      },
                      gap: 2,
                      maxHeight: '400px',
                      overflowY: 'auto',
                      p: 1,
                    }}
                  >
                    {frameItems.map((item) => {
                      const isSelected = selectedId === item.uniqueId;
                      const capturedTs = getCapturedTimestamp ? getCapturedTimestamp(item.log) : null;
                      const fallbackSrc =
                        item.image && item.image !== ANONYMOUS_IMAGE ? item.image : null;

                      return (
                        <Card
                          key={item.uniqueId}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            border: isSelected ? `3px solid ${primary}` : '1px solid',
                            borderColor: isSelected ? primary : 'divider',
                            '&:hover': {
                              boxShadow: 3,
                              borderColor: primary,
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => setBucketManualMarkSelectedAuditId(item.uniqueId)}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              paddingTop: '100%',
                              bgcolor: '#f5f5f5',
                              overflow: 'hidden',
                            }}
                          >
                            <BucketManualMarkFrameImage
                              auditId={item.uniqueId}
                              fallbackSrc={fallbackSrc}
                              anonymousImage={ANONYMOUS_IMAGE || '/anonymous.webp'}
                            />
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: primary,
                                  color: '#fff',
                                  borderRadius: '50%',
                                  width: 28,
                                  height: 28,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: primary,
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                            {item.isMain && (
                              <Chip
                                label="Main"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  left: 4,
                                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                                  color: '#fff',
                                  fontSize: '0.65rem',
                                  height: 18,
                                }}
                              />
                            )}
                            {item.log?.camera && (
                              <Chip
                                label={String(item.log.camera).toUpperCase()}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  left: 4,
                                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                                  color: '#fff',
                                  fontSize: '0.65rem',
                                  height: 18,
                                }}
                              />
                            )}
                          </Box>
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', display: 'block' }}>
                              {capturedTs?.iso
                                ? formatDate(capturedTs.iso, true, true, false, true)
                                : '—'}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}

              <TextField
                label="Employee"
                fullWidth
                required
                value={employeeDisplay}
                InputProps={{ readOnly: true }}
                helperText="Matched employee from this audit log (cannot be changed)"
              />
              <TextField
                label="Remark"
                required
                fullWidth
                multiline
                minRows={2}
                value={bucketManualMarkRemark}
                onChange={(e) => setBucketManualMarkRemark(e.target.value)}
                placeholder="Reason for manually marking attendance on this capture"
              />
            </Stack>
            ) : null}
          </UniversalModal>
        );
      })()}

      {/* Mark Attendance Dialog */}
      <UniversalModal
        open={markAttendanceDialog}
        onClose={handleCloseMarkAttendance}
        maxWidth="sm"
        fullWidth
        title="Mark Attendance"
        footer={
          <>
            <Button onClick={handleCloseMarkAttendance}>Cancel</Button>
            <Button
              onClick={handleMarkAttendanceSubmit}
              variant="contained"
              disabled={
                markAttendanceSubmitting ||
                (!disableCompanySelect && companyOptions.length > 1 && !markAttendanceCompanyId) ||
                !markAttendancePersonId ||
                (showOnlyCheckIn
                  ? !markAttendanceCheckIn
                  : showOnlyCheckOut
                    ? !markAttendanceCheckOut
                    : (!markAttendanceCheckIn && !markAttendanceCheckOut)) ||
                !markAttendanceRemark.trim()
              }
            >
              {markAttendanceSubmitting ? 'Marking...' : 'Mark Attendance'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {isMissingEntryMarkMode
              ? 'Mark missing attendance using the allowed time window from missing-entry search.'
              : 'Manually mark attendance for an employee with custom check-in and check-out times.'}
          </Typography>

          {companyOptions.length > 0 && (
            <FormControl fullWidth>
              <InputLabel id="mark-attendance-company-label">Select company</InputLabel>
              <Select
                labelId="mark-attendance-company-label"
                value={markAttendanceCompanyId}
                label="Select company"
                disabled={disableCompanySelect}
                onChange={(e) => {
                  setMarkAttendanceCompanyId(e.target.value);
                  setMarkAttendancePersonId('');
                  setMarkAttendancePersonName('');
                  loadEmployees(e.target.value);
                }}
              >
                {companyOptions.length > 1 && (
                  <MenuItem value="">
                    <em>Select company</em>
                  </MenuItem>
                )}
                {companyOptions.map((cfg) => (
                  <MenuItem key={cfg.config_id || cfg.company_id || 'default-mark'} value={cfg.company_id || ''}>
                    {cfg.company_name || 'Default Company'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Autocomplete
            options={sortedEmployees}
            loading={employeesLoading}
            disabled={disableEmployeeSelect || (companyOptions.length > 1 && !markAttendanceCompanyId)}
            filterOptions={filterEmployeePickerOptions}
            getOptionLabel={(option) => {
              const code = option?.employee_code ? ` (${option.employee_code})` : '';
              return `${option?.name || 'Unknown'}${code}`;
            }}
            value={
              employees.find(e => e.person_id === markAttendancePersonId) ||
              (disableEmployeeSelect && markAttendancePersonId
                ? {
                    person_id: markAttendancePersonId,
                    name: markAttendancePersonName || 'Selected Employee',
                    employee_code: markAttendanceContext?.employeeCode || '',
                  }
                : null)
            }
            onChange={(e, newValue) => {
              if (newValue) {
                setMarkAttendancePersonId(newValue.person_id);
                setMarkAttendancePersonName(newValue.name);
              } else {
                setMarkAttendancePersonId('');
                setMarkAttendancePersonName('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employee"
                placeholder="Search by name, ID, or Employee Code"
                helperText={
                  disableEmployeeSelect
                    ? 'Employee is locked for missing-entry correction.'
                    : companyOptions.length > 1 && !markAttendanceCompanyId
                    ? 'Choose a company first to see employees for that company.'
                    : undefined
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {employeesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {!showOnlyCheckOut && (
          <TextField
            label="Check In"
            value={markAttendanceCheckIn ? format(new Date(markAttendanceCheckIn), 'dd MMM yyyy, hh:mm a') : ''}
            placeholder="Click calendar to select date & time"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Login fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCalendarType('checkin');
                      if (markAttendanceCheckIn) {
                        setSelectedAttendanceDate(new Date(markAttendanceCheckIn));
                      } else {
                        setSelectedAttendanceDate(getMissingEntryDefaultSelection('checkin'));
                      }
                      setAttendanceCalendarOpen(true);
                    }}
                  >
                    <CalendarMonth fontSize="small" color="primary" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            fullWidth
          />
          )}

          {!showOnlyCheckIn && (
          <TextField
            label="Check Out"
            value={markAttendanceCheckOut ? format(new Date(markAttendanceCheckOut), 'dd MMM yyyy, hh:mm a') : ''}
            placeholder="Click calendar to select date & time"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Logout fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCalendarType('checkout');
                      if (markAttendanceCheckOut) {
                        setSelectedAttendanceDate(new Date(markAttendanceCheckOut));
                      } else {
                        setSelectedAttendanceDate(getMissingEntryDefaultSelection('checkout'));
                      }
                      setAttendanceCalendarOpen(true);
                    }}
                  >
                    <CalendarMonth fontSize="small" color="primary" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            fullWidth
          />
          )}

          {hasConstraintRange && (
            <Typography variant="caption" color="text.secondary">
              Allowed window: {format(constraintStart, 'dd MMM yyyy, hh:mm a')} to {format(constraintEnd, 'dd MMM yyyy, hh:mm a')}
            </Typography>
          )}

          <TextField
            label="Remark"
            value={markAttendanceRemark}
            onChange={(e) => {
              let val = e.target.value;
              // Capitalize first letter of the remark
              if (val.length > 0) {
                val = val.charAt(0).toUpperCase() + val.slice(1);
              }
              setMarkAttendanceRemark(val);
            }}
            placeholder="Add a note or reason for manual attendance"
            multiline
            rows={2}
            fullWidth
            required
            helperText="Required - add a remark for this attendance entry"
          />
        </Box>
      </UniversalModal>

      {/* Centered Calendar Dialog for Mark Attendance */}
      <UniversalModal
        open={attendanceCalendarOpen}
        onClose={() => setAttendanceCalendarOpen(false)}
        maxWidth="xs"
        fullWidth
        paperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
        titleSx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
        header={
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {calendarType === 'checkin' ? <Login color="primary" /> : <Logout color="primary" />}
              <Typography variant="h6">
                Select {calendarType === 'checkin' ? 'Check In' : 'Check Out'} Time
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setAttendanceCalendarOpen(false)}>
              <Close />
            </IconButton>
          </>
        }
        footer={
          <>
            <Button
              onClick={() => {
                if (calendarType === 'checkin') {
                  setMarkAttendanceCheckIn('');
                } else {
                  setMarkAttendanceCheckOut('');
                }
                setAttendanceCalendarOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Validate that selected date/time is not in the future
                const now = new Date();
                if (selectedAttendanceDate > now) {
                  show('Cannot select future date/time', 'error');
                  return;
                }
                if (!isInConstraintRange(selectedAttendanceDate)) {
                  show('Selected time must be within the missing-entry window', 'error');
                  return;
                }

                const formattedDateTime = format(selectedAttendanceDate, "yyyy-MM-dd'T'HH:mm");
                if (calendarType === 'checkin') {
                  setMarkAttendanceCheckIn(formattedDateTime);
                } else {
                  setMarkAttendanceCheckOut(formattedDateTime);
                }
                setAttendanceCalendarOpen(false);
              }}
            >
              Confirm
            </Button>
          </>
        }
        actionsSx={{ px: 3, pb: 2 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 1 }}>
          <Box
            sx={{
              '& .rdrCalendarWrapper': {
                width: '280px !important',
                fontSize: '12px',
              },
              '& .rdrMonth': {
                width: '280px !important',
              },
              '& .rdrMonthAndYearWrapper': {
                height: '50px',
                paddingTop: '8px',
              },
              '& .rdrDay': {
                height: '2.5em',
              },
              '& .rdrWeekDays': {
                fontSize: '11px',
              },
            }}
          >
            <Calendar
              date={selectedAttendanceDate}
              onChange={(date) => {
                if (!isInConstraintRange(date)) {
                  show('Selected date is outside allowed missing-entry window', 'error');
                  return;
                }
                setSelectedAttendanceDate(date);
              }}
              color={primary}
              minDate={hasConstraintRange ? constraintStart : undefined}
              maxDate={calendarMaxDate}
            />
          </Box>
          <Divider sx={{ width: '100%' }} />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
            <AccessTime fontSize="small" color="primary" />
            <TextField
              type="time"
              value={format(selectedAttendanceDate, 'HH:mm')}
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(':');
                const newDate = new Date(selectedAttendanceDate);
                newDate.setHours(parseInt(hours), parseInt(minutes));

                // Prevent future times
                const now = new Date();
                if (newDate > now) {
                  show('Cannot select future time', 'error');
                  return;
                }
                if (!isInConstraintRange(newDate)) {
                  show('Selected time must be within the missing-entry window', 'error');
                  return;
                }

                setSelectedAttendanceDate(newDate);
              }}
              size="small"
              fullWidth
              InputProps={{
                sx: { fontWeight: 500 },
                inputProps: {
                  max: format(
                    new Date(selectedAttendanceDate).toDateString() === new Date().toDateString()
                      ? new Date()
                      : new Date(
                          selectedAttendanceDate.getFullYear(),
                          selectedAttendanceDate.getMonth(),
                          selectedAttendanceDate.getDate(),
                          23,
                          59
                        ),
                    'HH:mm'
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </UniversalModal>

      {/* Possible Match pop-up: current capture (left) — branch lines — top matchers (right) */}
      <UniversalModal
        open={possibleMatchModal.open}
        onClose={closePossibleMatchModal}
        maxWidth="md"
        fullWidth
        paperProps={{ sx: { borderRadius: 2, maxWidth: 820 } }}
        dialogProps={{ scroll: 'body' }}
        contentSx={{ overflow: 'visible', pt: 2.5, pb: 2 }}
        titleSx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        header={
          <>
            <Typography variant="h6">Possible Match</Typography>
            <IconButton aria-label="Close" onClick={closePossibleMatchModal} size="small" sx={{ ml: 1 }}>
              <Close />
            </IconButton>
          </>
        }
        contentDividers
        actionsSx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}
        footer={
          <>
            <Box />
            <Stack direction="row" spacing={1}>
              {showVisionReviewUI && (
                <Button
                  variant="outlined"
                  startIcon={visionReviewLoading ? <CircularProgress size={16} color="inherit" /> : <SmartToy />}
                  disabled={visionReviewLoading || typeof onVisionReview !== 'function'}
                  onClick={() => {
                    if (typeof onVisionReview === 'function' && possibleMatchModal.log) {
                      const force = Boolean(possibleMatchModal.log.request_metadata?.vision_review?.reviewed);
                      onVisionReview(possibleMatchModal.log, { source: 'modal', force });
                    }
                  }}
                >
                  {visionReviewLoading
                    ? 'Matching…'
                    : (possibleMatchModal.log?.request_metadata?.vision_review?.reviewed ? 'Re-run AI Matching' : 'AI Matching')}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<Psychology />}
                onClick={(e) => {
                  if (possibleMatchModal.log?.audit_id) {
                    possibleMatchTrainActiveRef.current = true;
                    setTrainAuditId(possibleMatchModal.log.audit_id);
                    setTrainMenuAnchor(e.currentTarget);
                  }
                }}
              >
                Train
              </Button>
              <Button
                variant="contained"
                disabled={!possibleMatchSelectedId || possibleMatchMarkSubmitting}
                onClick={() => {
                  const log = possibleMatchModal.log;
                  const selected = employees.find((e) => e.person_id === possibleMatchSelectedId);
                  const personName =
                    selected?.name ||
                    log?.request_metadata?.top_candidates?.find((c) => String(c.person_id) === String(possibleMatchSelectedId))?.name ||
                    '';
                  openConfirmMarkAttendance('possible_match_modal', personName);
                }}
              >
                {possibleMatchMarkSubmitting ? 'Marking…' : 'Mark attendance'}
              </Button>
            </Stack>
          </>
        }
      >
        {possibleMatchModal.log && (() => {
          const candidates = Array.isArray(possibleMatchModal.log.request_metadata?.top_candidates)
            ? possibleMatchModal.log.request_metadata.top_candidates
            : [];
          // Always show exactly 3 slots: fill with candidates, then placeholders
          const displayCandidates = [...candidates.slice(0, 3), null, null, null].slice(0, 3);
          const topCandidateIds = new Set(
            displayCandidates
              .filter(Boolean)
              .map((candidate) => String(candidate.person_id).trim())
              .filter(Boolean)
          );
          const n = 3;
          const matchersHeaderHeight = 28;
          const matcherCardMinHeight = 100;
          const matcherCardGap = 10;
          const matcherListHeight = (n * matcherCardMinHeight) + ((n - 1) * matcherCardGap);
          return (
            <Stack spacing={2.5}>
            {isAttendanceRevokedOnLog(possibleMatchModal.log) && getAttendanceRevokedReopenHint(possibleMatchModal.log) && (
              <Alert severity="info">
                {getAttendanceRevokedReopenHint(possibleMatchModal.log)}
              </Alert>
            )}
            {showVisionReviewUI && (
              <VisionReviewRecommendation
                visionReview={possibleMatchModal.log.request_metadata?.vision_review}
                topCandidates={possibleMatchModal.log.request_metadata?.top_candidates}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                minHeight: { sm: 280 },
              }}
            >
              {/* Left: image area — always shows current capture (does not change on selection) */}
              <Box
                sx={{
                  flex: '0 0 200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Current capture
                </Typography>
                <Box
                  sx={{
                    width: 160,
                    height: 160,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'grey.200',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    component="img"
                    src={possibleMatchModal.image || ANONYMOUS_IMAGE}
                    alt="Current capture"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Box>

              {/* Middle: branch lines (tree-style curved lines to each matcher) */}
              <Box
                sx={{
                  flex: '0 0 72px',
                  position: 'relative',
                  minWidth: 72,
                  mt: `${matchersHeaderHeight}px`,
                  height: `${matcherListHeight}px`,
                }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0 }}
                >
                  {displayCandidates.map((_, i) => {
                    const yCenterPx = (matcherCardMinHeight / 2) + (i * (matcherCardMinHeight + matcherCardGap));
                    const yEnd = (yCenterPx / matcherListHeight) * 100;
                    const yStart = 50;
                    const cx = 50;
                    const path = `M 0 ${yStart} Q ${cx} ${yStart}, ${cx} ${yEnd} Q ${cx} ${yEnd}, 100 ${yEnd}`;
                    return (
                      <path
                        key={i}
                        d={path}
                        fill="none"
                        stroke="#9e9e9e"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                      />
                    );
                  })}
                </svg>
              </Box>

              {/* Right: top matchers — always 3 slots (candidate or "No other candidate") */}
              <Box
                sx={{
                  flex: '1 1 0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ height: `${matchersHeaderHeight}px`, display: 'flex', alignItems: 'center' }}
                >
                  Top matchers
                </Typography>
                <RadioGroup
                  value={possibleMatchSelectedId || ''}
                  onChange={(e) => setPossibleMatchSelectedId(e.target.value || null)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${matcherCardGap}px`,
                    minHeight: `${matcherListHeight}px`,
                  }}
                >
                  {displayCandidates.map((c, idx) => {
                    const isPlaceholder = c == null;
                    const pid = c?.person_id ? String(c.person_id).trim() : null;
                    const candidateImage = pid ? possibleMatchCandidateImages[pid] : null;
                    return (
                      <Paper
                        key={pid != null ? pid : `placeholder-${idx}`}
                        variant="outlined"
                        sx={{
                          p: 0.75,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.5,
                          cursor: isPlaceholder ? 'default' : 'pointer',
                          opacity: isPlaceholder ? 0.7 : 1,
                          minHeight: `${matcherCardMinHeight}px`,
                          border: !isPlaceholder && possibleMatchSelectedId === pid ? '2px solid' : '1px solid',
                          borderColor: !isPlaceholder && possibleMatchSelectedId === pid ? 'primary.main' : 'divider',
                          bgcolor: !isPlaceholder && possibleMatchSelectedId === pid ? 'action.selected' : 'background.paper',
                        }}
                        onClick={isPlaceholder ? undefined : () => setPossibleMatchSelectedId(pid || null)}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              width: 72,
                              height: 88,
                              flexShrink: 0,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: 'grey.300',
                            }}
                          >
                            <Box
                              component="img"
                              src={candidateImage || ANONYMOUS_IMAGE}
                              alt={isPlaceholder ? '—' : (c.name || 'Candidate')}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                          <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {isPlaceholder ? 'No other candidate' : (c.name || 'Unknown')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {isPlaceholder ? '—' : `Matching score: ${Number(c.score).toFixed(2)}`}
                            </Typography>
                          </Box>
                        </Box>
                        {isPlaceholder ? (
                          <Radio size="small" disabled sx={{ p: 0.25, flexShrink: 0 }} />
                        ) : (
                          <Radio size="small" value={pid || ''} sx={{ p: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                        )}
                      </Paper>
                    );
                  })}
                </RadioGroup>
              </Box>
            </Box>

            <PossibleMatchManualPersonSelect
              companyOptions={companyOptions}
              manualCompanyId={possibleMatchManualCompanyId}
              setManualCompanyId={setPossibleMatchManualCompanyId}
              selectedPersonId={possibleMatchSelectedId}
              setSelectedPersonId={setPossibleMatchSelectedId}
              sortedEmployees={sortedEmployees}
              employeesLoading={employeesLoading}
              loadEmployees={loadEmployees}
              topCandidateIds={topCandidateIds}
              sessionKey={possibleMatchModal.log?.audit_id || ''}
            />
            </Stack>
          );
        })()}
      </UniversalModal>

      {/* Confirm Mark Attendance (after selecting top match) */}
      <UniversalModal
        open={confirmMarkAttendanceOpen}
        onClose={closeConfirmMarkAttendance}
        maxWidth="xs"
        fullWidth
        title="Confirm mark attendance"
        paperProps={{ sx: { borderRadius: 2 } }}
        actionsSx={{ px: 2, pb: 2 }}
        footer={
          <>
            <Button onClick={closeConfirmMarkAttendance}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmMarkAttendanceSubmit}
              disabled={
                possibleMatchMarkSubmitting ||
                timingDialogPossibleMatchMarkSubmitting ||
                bucketManualMarkSubmitting
              }
            >
              {(confirmMarkAttendanceSource === 'possible_match_modal' && possibleMatchMarkSubmitting) ||
              (confirmMarkAttendanceSource === 'timing_dialog' && timingDialogPossibleMatchMarkSubmitting) ||
              (confirmMarkAttendanceSource === 'bucket_manual_mark' && bucketManualMarkSubmitting)
                ? 'Marking…'
                : 'Confirm'}
            </Button>
          </>
        }
      >
        <Typography>
          Mark attendance for <strong>{confirmMarkAttendancePersonName}</strong>?
        </Typography>
      </UniversalModal>

      {/* Train Menu */}
      <Menu
        anchorEl={trainMenuAnchor}
        open={Boolean(trainMenuAnchor)}
        onClose={handleTrainClose}
      >
        <MenuItem onClick={handleOpenAddToEmployee}>Add to Existing Employee</MenuItem>
        <MenuItem onClick={handleOpenCreateEmployee}>Create New Employee</MenuItem>
      </Menu>

      <UniversalModal
        open={Boolean(createProfileTypeDialogOpen)}
        onClose={() => setCreateProfileTypeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        title=""
        footer={
          <Button
            onClick={() => setCreateProfileTypeDialogOpen(false)}
            sx={{
              px: 3.5,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#f1f2f6',
              color: '#6b7280',
              '&:hover': { bgcolor: '#e8eaf0' },
            }}
          >
            Cancel
          </Button>
        }
        paperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            maxWidth: 620,
          },
        }}
      >
        <Box sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#f0effe',
                  color: '#4f46e5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.25,
                }}
              >
                <PersonAddAlt1Outlined sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.25, fontSize: 38/2 }}>
                  Create New Employee
                </Typography>
                <Typography sx={{ color: '#7b8197', fontWeight: 400, fontSize: 15 }}>
                  Select the type of employee you want to create.
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setCreateProfileTypeDialogOpen(false)} sx={{ color: '#8c92ab', p: 0.25 }}>
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Stack spacing={0.9}>
            <Box
              onClick={() => {
                setCreateProfileTypeDialogOpen(false);
                handleOpenCreateEmployeeProfile();
              }}
              sx={{
                p: 1.2,
                borderRadius: 1.6,
                border: '1px solid #6f7db9',
                // bgcolor: '#f5f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 42, height: 42, borderRadius: 1.25, bgcolor: '#e7ebfa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5a96' }}>
                  <PeopleIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 17, fontWeight: 500, color: '#40518A', lineHeight: 1.15 }}>Employees</Typography>
                  <Typography sx={{ fontSize: 14, color: '#6c738a' }}>Create a new internal employee.</Typography>
                </Box>
              </Stack>
              <ChevronRight sx={{ color: '#4b5a96', fontSize: 28 }} />
            </Box>

            {vendorManagementEnabled && (
              <Box
                onClick={() => {
                  setCreateProfileTypeDialogOpen(false);
                  handleOpenCreateVendor();
                }}
                sx={{
                  p: 1.2,
                  borderRadius: 1.6,
                  border: '1px solid #6f7db9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 42, height: 42, borderRadius: 1.25, bgcolor: '#eaf8f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1f9d72' }}>
                    <AssignmentIndIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 500, color: '#40518A', lineHeight: 1.15 }}>Vendor</Typography>
                    <Typography sx={{ fontSize: 14, color: '#6c738a' }}>Create a new vendor.</Typography>
                  </Box>
                </Stack>
                <ChevronRight sx={{ color: '#4b5a96', fontSize: 28 }} />
              </Box>
            )}

            {visitorManagementEnabled && (
              <Box
                onClick={() => {
                  setCreateProfileTypeDialogOpen(false);
                  handleOpenCreateVisitor();
                }}
                sx={{
                  p: 1.2,
                  borderRadius: 1.6,
                  border: '1px solid #6f7db9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 42, height: 42, borderRadius: 1.25, bgcolor: '#edf2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5a96' }}>
                    <VisitorIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 500, color: '#40518A', lineHeight: 1.15 }}>Visitor</Typography>
                    <Typography sx={{ fontSize: 14, color: '#6c738a' }}>Create a new visitor.</Typography>
                  </Box>
                </Stack>
                <ChevronRight sx={{ color: '#4b5a96', fontSize: 28 }} />
              </Box>
            )}
            
            <Box
              onClick={() => {
                setCreateProfileTypeDialogOpen(false);
                handleOpenCreateIntrusion();
              }}
              sx={{
                p: 1.2,
                borderRadius: 1.6,
                border: '1px solid #6f7db9',
                // bgcolor: '#fffaf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 42, height: 42, borderRadius: 1.25, bgcolor: '#fff2e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef8e1a' }}>
                  <IntrusionAttendanceIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 17, fontWeight: 500, color: '#40518A', lineHeight: 1.15 }}>Intrusion</Typography>
                  <Typography sx={{ fontSize: 14, color: '#6c738a' }}>Create a new intrusion record.</Typography>
                </Box>
              </Stack>
              <ChevronRight sx={{ color: '#4b5a96', fontSize: 28 }} />
            </Box>
          </Stack>
        </Box>
      </UniversalModal>

      {/* Image preview dialog */}
      {previewOpen && (
        <Box sx={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={closePreview}>
          <Box sx={{ background: '#fff', p: 1.5, borderRadius: 1, maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            {previewUrl ? (
              <img src={previewUrl} alt="Recognition" style={{ maxWidth: '85vw', maxHeight: '80vh', display: 'block' }} />
            ) : (
              <Typography>Loading…</Typography>
            )}
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Button size="small" onClick={closePreview}>Close</Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AuditDialogs;
export { FindPersonSearchFromTileModal } from './FindPersonSearchFromTileModal';
